/**
 * CoachPlaybook - Playbook Studio
 * Version: 0.6.0
 * Description: Broadcast-quality play designer with full animation engine.
 *              60/40 split layout with contextual action wheel, timeline scrubber, and tool strip.
 *              Ported animation system: Catmull-Rom paths, chevron indicators, pass/shot/screen
 *              rendering, ball tracking, defender animation, per-player speed support.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import GIF from 'gif.js';
import PlaySurfaceRenderer from '../court/PlaySurfaceRenderer';
import ActionWheel from './ActionWheel';
import TimelineScrubber from './TimelineScrubber';
import ScreenRecorder from './ScreenRecorder';
import ToolStrip from './ToolStrip';
import ControlPanel from './ControlPanel';
import RosterPicker from '../roster/RosterPicker';
import { useSport } from '../common/SportContext';
import { createPlayFromSport, deletePlay } from '../../utils/plays';
import { getPlaysBySport, savePlayForSport, getSetting, setSetting } from '../../utils/db';
import { updatePlayer } from '../../utils/players/playerUtils';
import { ASPECT_RATIOS } from '../court/PlaySurfaceRenderer';
import {
  smoothPath,
  simplifyPath,
  getPathLength,
  getChevronPositions,
  getPointOnLine,
  getStepBadgePosition,
  getOffsetStartPoint,
  getPlayerPositionAtStep,
  getAnimatedPosition,
  getBallHolderAtStep,
  getDefenderPosition,
  getPassBallPosition,
  getShotBallPosition,
  getBallEmoji,
  getShotTarget,
  ANIMATION_STYLES
} from '../../utils/plays/animationEngine';

// Constants
const GHOST_RADIUS = 3;

// Scale coordinates from 0-100 data space to viewBox space
const getAspectKey = (sport, view) => {
  if (sport === 'basketball' && view === 'half') return 'basketballHalf';
  if (sport === 'soccer' && view === 'half') return 'soccerHalf';
  return sport;
};

const getScaleY = (sport, view) => {
  // Scale Y always maps data-space (0-100) to the FULL surface coordinate system
  // For soccer half, the viewBox crop handles the visual zoom — coordinates stay the same
  const fullKey = sport === 'basketball' && view === 'half' ? 'basketballHalf' : sport;
  const aspect = ASPECT_RATIOS[fullKey] || ASPECT_RATIOS.basketballHalf;
  return aspect.h / 100;
};

// Display name: player name if assigned, otherwise position label
const getDisplayName = (pos) => pos?.playerName || pos?.positionLabel || '?';

// Rendered soccer ball SVG element (replaces emoji)
const BallImage = ({ x = 0, y = 0, size = 3, opacity = 1 }) => (
  <image
    href="/sprites/soccer_ball.png"
    x={x - size / 2} y={y - size / 2}
    width={size} height={size}
    opacity={opacity}
    style={{ pointerEvents: 'none' }}
  />
);

// 8-direction sprite: determine facing from path endpoints
const SPRITE_DIRECTIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const DEFAULT_SPRITE_SIZE = 60; // viewBox units for the sprite image
const BALL_SIZE = 3.5; // consistent ball size across all renders
// Animation sprite frame counts
const ACTION_FRAMES = {
  run: 13,
  kick: 23,
  pass: 18,
  receive: 3, // Just the trap — clean and quick
};

// Get sprite frame index for looping animations (run)
const getLoopFrame = (progress, action = 'run') => {
  const total = ACTION_FRAMES[action] || 13;
  const cycles = 3;
  const t = (progress * cycles) % 1;
  return Math.floor(t * total) % total;
};

// Get sprite frame index for one-shot animations (kick, pass, receive)
const getOneShotFrame = (progress, action) => {
  const total = ACTION_FRAMES[action] || 18;
  return Math.min(Math.floor(progress * total), total - 1);
};

// Check if a player has a path on the current step
const isPlayerMoving = (positionId, currentStep, paths, progress) => {
  if (progress == null || currentStep === 0) return false;
  return (paths || []).some(p => p.positionId === positionId && (p.stepNumber || 1) === currentStep);
};

// Get the action a player is performing on the current step
// Returns: { action: 'run'|'kick'|'pass'|'receive'|'idle', frameIdx: '00' }
// Timing phases for pass/shoot steps:
// 0.0  - 0.15: Passer wind-up (ball at feet)
// 0.15:        Foot contact — ball launches
// 0.15 - 0.35: Passer follow-through (animation continues)
// 0.15 - 0.6:  Ball travels to receiver
// 0.55 - 1.0:  Receiver traps/controls (starts just before ball arrives)
const PASS_PHASE = { start: 0, end: 0.35 };    // passer full animation
const BALL_LAUNCH = 0.15;                        // ball leaves foot
const BALL_PHASE = { start: 0.15, end: 0.6 };   // ball in air
const RECV_PHASE = { start: 0.55, end: 1.0 };   // receiver starts just before ball lands

const getPlayerAction = (positionId, currentStep, paths, passes, progress) => {
  if (progress == null || currentStep === 0) return { action: 'idle', frameIdx: null };

  // Check if player is shooting or passing on this step
  const playerPass = (passes || []).find(
    p => p.fromPositionId === positionId && (p.stepNumber || 1) === currentStep
  );
  if (playerPass) {
    // Passer/shooter: animate during PASS_PHASE, then idle
    if (progress <= PASS_PHASE.end) {
      const action = playerPass.type === 'shoot' ? 'kick' : 'pass';
      const localProgress = progress / PASS_PHASE.end; // 0→1 within pass phase
      const frame = getOneShotFrame(localProgress, action);
      return { action, frameIdx: String(frame).padStart(2, '0') };
    }
    return { action: 'idle', frameIdx: null };
  }

  // Check if player is receiving on this step
  const receiving = (passes || []).find(
    p => p.toPositionId === positionId && (p.stepNumber || 1) === currentStep
  );
  if (receiving) {
    // Receiver: animate during RECV_PHASE, idle before
    if (progress >= RECV_PHASE.start) {
      const localProgress = (progress - RECV_PHASE.start) / (RECV_PHASE.end - RECV_PHASE.start);
      const frame = getOneShotFrame(Math.min(localProgress, 1), 'receive');
      return { action: 'receive', frameIdx: String(frame).padStart(2, '0') };
    }
    return { action: 'idle', frameIdx: null };
  }

  // Check if player is moving (has a path)
  const moving = (paths || []).some(
    p => p.positionId === positionId && (p.stepNumber || 1) === currentStep
  );
  if (moving) {
    const frame = getLoopFrame(progress, 'run');
    return { action: 'run', frameIdx: String(frame).padStart(2, '0') };
  }

  return { action: 'idle', frameIdx: null };
};

const getFacingDirection = (positionId, currentStep, positions, paths, progress, ballPos, passes) => {
  const playerPaths = (paths || [])
    .filter(p => p.positionId === positionId && p.points?.length >= 2)
    .sort((a, b) => (a.stepNumber || 1) - (b.stepNumber || 1));

  // Position-level facing override always wins
  const pos = positions?.find(p => p.id === positionId);
  if (pos?.facingDirection) return pos.facingDirection;

  // Pass/receive: face toward the other player involved in the pass
  if (passes && currentStep > 0) {
    const asPass = (passes || []).find(p => p.fromPositionId === positionId && (p.stepNumber || 1) === currentStep);
    if (asPass && asPass.toPositionId) {
      const receiver = positions?.find(p => p.id === asPass.toPositionId);
      if (receiver && pos) {
        const dx = (receiver.x || receiver.startX || 0) - (pos.x || pos.startX || 0);
        const dy = (receiver.y || receiver.startY || 0) - (pos.y || pos.startY || 0);
        if (Math.abs(dx) > 0.3 || Math.abs(dy) > 0.3) return angleToBearing(dx, dy);
      }
    }
    const asRecv = (passes || []).find(p => p.toPositionId === positionId && (p.stepNumber || 1) === currentStep);
    if (asRecv) {
      const passer = positions?.find(p => p.id === asRecv.fromPositionId);
      if (passer && pos) {
        const dx = (passer.x || passer.startX || 0) - (pos.x || pos.startX || 0);
        const dy = (passer.y || passer.startY || 0) - (pos.y || pos.startY || 0);
        if (Math.abs(dx) > 0.3 || Math.abs(dy) > 0.3) return angleToBearing(dx, dy);
      }
    }
  }

  // Find the path for the current step (active movement)
  const currentPath = playerPaths.find(p => (p.stepNumber || 1) === currentStep);
  if (currentPath) {
    if (currentPath.facingOverride) return currentPath.facingOverride;
    const pts = currentPath.points;
    if (pts.length >= 2) {
      const prog = progress != null ? progress : 1;
      // During active movement (progress < 1), face movement direction
      if (prog < 1) {
        const idx = Math.min(Math.floor(prog * (pts.length - 1)), pts.length - 2);
        const from = pts[idx];
        const to = pts[idx + 1];
        if (from && to) {
          const dx = to.x - from.x;
          const dy = to.y - from.y;
          if (Math.abs(dx) > 0.3 || Math.abs(dy) > 0.3) {
            return angleToBearing(dx, dy);
          }
        }
      }
      // At end of run — face toward ball if available
      if (ballPos) {
        const endPt = pts[pts.length - 1];
        const dx = ballPos.x - endPt.x;
        const dy = ballPos.y - endPt.y;
        if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
          return angleToBearing(dx, dy);
        }
      }
      // Fallback: face last segment direction
      const last = pts[pts.length - 1];
      const prev = pts[pts.length - 2];
      if (last && prev) {
        const dx = last.x - prev.x;
        const dy = last.y - prev.y;
        if (Math.abs(dx) > 0.3 || Math.abs(dy) > 0.3) {
          return angleToBearing(dx, dy);
        }
      }
    }
  }

  // No path for current step — keep facing from most recent completed path
  const activePath = playerPaths.filter(p => (p.stepNumber || 1) < currentStep).pop();
  if (activePath) {
    if (activePath.facingOverride) return activePath.facingOverride;
    // Face last segment direction of their most recent run
    const pts = activePath.points;
    const last = pts[pts.length - 1];
    const prev = pts[pts.length - 2] || pts[0];
    if (last && prev) {
      const dx = last.x - prev.x;
      const dy = last.y - prev.y;
      if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
        return angleToBearing(dx, dy);
      }
    }
  }

  return 'S';
};

// Convert compass click to facing direction (for manual override)
const angleToBearing = (dx, dy) => {
  const angle = Math.atan2(dx, -dy) * (180 / Math.PI);
  const normalized = ((angle % 360) + 360) % 360;
  const idx = Math.round(normalized / 45) % 8;
  return SPRITE_DIRECTIONS[idx];
};

// Generate a unique SVG filter ID for color tinting via feColorMatrix
// White pixels become the target color. Grey pixels become darker version.
// This is pixel-accurate — no hue-shift artifacts.
let _filterCounter = 0;
const getTintFilterId = (hexColor) => {
  if (!hexColor) return null;
  return `tint-${hexColor.replace('#', '')}-${_filterCounter++}`;
};

const hexToRgb01 = (hex) => {
  if (!hex) return { r: 1, g: 1, b: 1 };
  return {
    r: parseInt(hex.slice(1, 3), 16) / 255,
    g: parseInt(hex.slice(3, 5), 16) / 255,
    b: parseInt(hex.slice(5, 7), 16) / 255,
  };
};

// Legacy CSS filter for non-SVG contexts (roster cards etc)
const getColorFilter = (hexColor) => {
  if (!hexColor) return 'none';
  const { r, g, b } = hexToRgb01(hexColor);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    s = l > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min);
    if (max === r) h = ((g - b) / (max - min)) + (g < b ? 6 : 0);
    else if (max === g) h = ((b - r) / (max - min)) + 2;
    else h = ((r - g) / (max - min)) + 4;
    h *= 60;
  }
  const brightness = Math.round(l * 100 + 20);
  return `sepia(1) hue-rotate(${Math.round(h - 50)}deg) saturate(${Math.round(s * 400 + 100)}%) brightness(${brightness}%)`;
};

const PlaybookStudio = ({ initialPlayId } = {}) => {
  const { currentSport, sportConfig, currentAgeGroup } = useSport();

  // Surface view and scale — needed by callbacks, must be defined early
  const surfaceViewEarly = sportConfig?.defaultView || 'half';
  const scaleYEarly = getScaleY(currentSport, surfaceViewEarly);
  const aspectKeyEarly = getAspectKey(currentSport, surfaceViewEarly);
  const aspectEarly = ASPECT_RATIOS[aspectKeyEarly] || { w: 100, h: 100 };

  // Play state
  const [plays, setPlays] = useState([]);
  const [currentPlay, setCurrentPlay] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Interaction state
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedDefender, setSelectedDefender] = useState(null);
  const [addToCurrentStep, setAddToCurrentStep] = useState(false); // true = simultaneous movement
  const [spriteSize, setSpriteSize] = useState(() => {
    const saved = localStorage.getItem('pb-spriteSize');
    return saved ? Number(saved) : DEFAULT_SPRITE_SIZE;
  });
  const [draggingDefender, setDraggingDefender] = useState(null);
  const [draggingBall, setDraggingBall] = useState(false);
  const [showRoutes, setShowRoutes] = useState(true);
  const [showJerseyNumbers, setShowJerseyNumbers] = useState(() => {
    const saved = localStorage.getItem('pb-showJerseyNumbers');
    return saved !== null ? saved === 'true' : true;
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const exportInProgressRef = useRef(false);
  const [pendingPassToSpace, setPendingPassToSpace] = useState(null); // waiting for receiver click
  const [showRosterPicker, setShowRosterPicker] = useState(false);
  const [rosterAssignTarget, setRosterAssignTarget] = useState(null);
  const [jerseyTemplates, setJerseyTemplates] = useState([]);
  const [colorMode, setColorMode] = useState('position'); // 'position' or 'team'
  const [showRecorder, setShowRecorder] = useState(false);

  // Persist sprite size and jersey number toggle across sessions
  useEffect(() => {
    localStorage.setItem('pb-spriteSize', String(spriteSize));
  }, [spriteSize]);
  useEffect(() => {
    localStorage.setItem('pb-showJerseyNumbers', String(showJerseyNumbers));
  }, [showJerseyNumbers]);

  const handleRosterAssign = useCallback((player) => {
    if (!rosterAssignTarget || !currentPlay) return;
    // Get the position label the player is being assigned to
    const targetSlot = currentPlay.positions.find(p => p.id === rosterAssignTarget);
    const positionLabel = targetSlot?.positionLabel || '';

    setCurrentPlay(prev => ({
      ...prev,
      positions: prev.positions.map(p =>
        p.id === rosterAssignTarget ? {
          ...p,
          playerId: player.id,
          playerName: `${player.firstName} ${player.lastName}`.trim(),
          jerseyNumber: player.jerseyNumber,
          avatar: player.avatar || null,
        } : p
      ),
      lastModified: new Date().toISOString(),
    }));

    // Save the position back to the player's roster record
    if (positionLabel && player.id) {
      updatePlayer(player.id, { ...player, position: positionLabel }, currentSport);
    }

    setShowRosterPicker(false);
    setRosterAssignTarget(null);
  }, [rosterAssignTarget, currentPlay, currentSport]);
  const [activeTool, setActiveTool] = useState('select');
  const [showDefenders, setShowDefenders] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [draggingPlayer, setDraggingPlayer] = useState(null);

  // Action wheel
  const [wheelVisible, setWheelVisible] = useState(false);
  const [wheelPosition, setWheelPosition] = useState({ x: 0, y: 0 });
  const [wheelActions, setWheelActions] = useState([]);
  const [wheelColor, setWheelColor] = useState('#3b82f6');
  const [wheelPlayerLabel, setWheelPlayerLabel] = useState('');

  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentDrawAction, setCurrentDrawAction] = useState(null);
  const [drawPoints, setDrawPoints] = useState([]);

  // Animation/timeline state
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  // Saved lineups
  const [lineups, setLineups] = useState([]);

  // Load lineups on mount
  useEffect(() => {
    const loadLineups = async () => {
      const saved = await getSetting(`lineups-${currentSport}`, []);
      setLineups(saved || []);
    };
    loadLineups();
  }, [currentSport]);

  // Undo/redo
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Refs
  const surfaceRef = useRef(null);
  const animationRef = useRef(null);

  // ============================================
  // CSS ANIMATION INJECTION
  // ============================================

  useEffect(() => {
    const styleId = 'playbook-studio-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = ANIMATION_STYLES;
      document.head.appendChild(style);
    }
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  // ============================================
  // DATA LOADING
  // ============================================

  useEffect(() => {
    const loadPlays = async () => {
      setIsLoading(true);
      setCurrentPlay(null);
      setSelectedPosition(null);
      setWheelVisible(false);
      try {
        const allPlays = await getPlaysBySport(currentSport);
        setPlays(allPlays);
        if (allPlays.length > 0) {
          const target = initialPlayId ? allPlays.find(p => p.id === initialPlayId) : null;
          setCurrentPlay(target || allPlays[0]);
        }
      } catch (err) {
        console.error('Failed to load plays:', err);
      }
      setIsLoading(false);
    };
    loadPlays();
  }, [currentSport]);

  // Load jersey templates for team color mode
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templates = await getSetting('jerseyTemplates') || [];
        setJerseyTemplates(templates);
      } catch (err) {
        console.error('Failed to load jersey templates:', err);
      }
    };
    loadTemplates();
  }, []);

  // Preload action sprite frames to avoid flicker during animation
  useEffect(() => {
    if (!currentPlay) return;
    const combos = new Set();
    (currentPlay.positions || []).forEach(p => {
      const j = p.jerseyColorPreset || 'blue';
      const s = p.shortsColorPreset || 'white';
      combos.add(`${j}_${s}`);
    });
    (currentPlay.defenders || []).forEach(d => {
      const j = d.jerseyColorPreset || (currentPlay.defJerseyPreset || 'red');
      const s = d.shortsColorPreset || (currentPlay.defShortsPreset || 'black');
      combos.add(`${j}_${s}`);
    });
    const dirs = SPRITE_DIRECTIONS;
    const actions = Object.entries(ACTION_FRAMES);
    combos.forEach(combo => {
      dirs.forEach(dir => {
        actions.forEach(([action, frameCount]) => {
          for (let f = 0; f < frameCount; f++) {
            const img = new Image();
            img.src = `/sprites/color/${combo}/player_${action}_${dir}_${String(f).padStart(2, '0')}.png`;
          }
        });
      });
    });
  }, [currentPlay?.id]);

  // ============================================
  // PLAY MANAGEMENT
  // ============================================

  const handleCreatePlay = useCallback(() => {
    const ageGroupConfig = sportConfig?.ageGroups?.find(ag => ag.id === currentAgeGroup);
    const newPlay = createPlayFromSport(sportConfig, {
      ageGroup: currentAgeGroup,
      playerCount: ageGroupConfig?.players
    });
    newPlay.name = `Play ${plays.length + 1}`;
    newPlay.playNumber = plays.length + 1;
    newPlay.createdAt = new Date().toISOString();
    setCurrentPlay(newPlay);
    setPlays(prev => [newPlay, ...prev]);
    savePlayForSport(newPlay, currentSport);
    setCurrentStep(0);
    setSelectedPosition(null);
    setWheelVisible(false);
  }, [sportConfig, currentAgeGroup, currentSport, plays.length]);

  const handlePlaySelect = useCallback(async (playId) => {
    const play = plays.find(p => p.id === playId);
    if (play) {
      setCurrentPlay(play);
      setCurrentStep(0);
      setSelectedPosition(null);
      setWheelVisible(false);
    }
  }, [plays]);

  const handlePlayDelete = useCallback(async (playId) => {
    await deletePlay(playId);
    setPlays(prev => prev.filter(p => p.id !== playId));
    if (currentPlay?.id === playId) {
      setCurrentPlay(null);
    }
  }, [currentPlay]);

  const handlePlayUpdate = useCallback((updatedPlay) => {
    setCurrentPlay(updatedPlay);
    setPlays(prev => prev.map(p => p.id === updatedPlay.id ? updatedPlay : p));
    savePlayForSport(updatedPlay, currentSport);
  }, [currentSport]);

  // ============================================
  // UNDO / REDO
  // ============================================

  const pushUndo = useCallback((play) => {
    setUndoStack(prev => [...prev.slice(-30), JSON.parse(JSON.stringify(play))]);
    setRedoStack([]);
  }, []);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0 || !currentPlay) return;
    setRedoStack(prev => [...prev, JSON.parse(JSON.stringify(currentPlay))]);
    const previous = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    setCurrentPlay(previous);
    savePlayForSport(previous, currentSport);
  }, [undoStack, currentPlay, currentSport]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    setUndoStack(prev => [...prev, JSON.parse(JSON.stringify(currentPlay))]);
    const next = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    setCurrentPlay(next);
    savePlayForSport(next, currentSport);
  }, [redoStack, currentPlay, currentSport]);

  // ============================================
  // SURFACE INTERACTIONS
  // ============================================

  const getActionsForPosition = useCallback((position) => {
    if (!sportConfig) return [];

    // Baseball: position-aware + age-group-gated actions
    if (sportConfig.positionActions && position) {
      const posId = position.positionLabel || position.id;
      const actionIds = sportConfig.positionActions[posId] || sportConfig.positionActions.batter || [];

      // Collect all actions into a lookup by ID
      const allActions = [
        ...(sportConfig.actions?.offense || []),
        ...(sportConfig.actions?.defense || []),
        ...(sportConfig.actions?.baserunning || []),
        ...(sportConfig.actions?.cutoffRelay || []),
        ...(sportConfig.actions?.transition || [])
      ];
      const actionMap = {};
      allActions.forEach(a => { actionMap[a.id] = a; });

      // Get blocked actions for current age group
      const blocked = new Set(
        (sportConfig.ageGroupBlockedActions?.[currentAgeGroup] || [])
      );

      // Resolve action IDs to action objects, filtering out blocked ones
      return actionIds
        .filter(id => !blocked.has(id))
        .map(id => actionMap[id])
        .filter(Boolean)
        .slice(0, 8);
    }

    // Context-aware actions based on ball possession
    const repositionAction = { id: '_reposition', name: 'Reposition', category: 'utility', icon: 'move' };
    const assignAction = { id: '_assign', name: 'Assign Player', category: 'utility', icon: 'replace' };
    const offenseActions = sportConfig.actions?.offense || [];
    const defenseActions = sportConfig.actions?.defense || [];
    const transitionActions = sportConfig.actions?.transition || [];

    // Use the animation engine's ball tracking — accounts for passes and dribbles
    const currentHolder = currentPlay?.positions
      ? getBallHolderAtStep(currentStep, currentPlay.positions, currentPlay.paths || [], currentPlay.passes || [])
      : null;
    const hasBall = position?.id === currentHolder;

    let contextActions;
    if (hasBall) {
      // Ball holder: core 8 = Reposition, Dribble, Pass, Through Ball, Shoot, Cross, Lob, Back Pass
      const priorityIds = ['dribble', 'pass', 'through-ball', 'shoot', 'cross', 'lob', 'back-pass'];
      const priority = priorityIds
        .map(id => offenseActions.find(a => a.id === id))
        .filter(Boolean);
      const rest = offenseActions.filter(a =>
        ['ball', 'finishing', 'set-piece'].includes(a.category) && !priorityIds.includes(a.id)
      );
      contextActions = [repositionAction, ...priority, ...rest];
    } else {
      // Off-ball: movement runs + finishing (header, volley off crosses/deflections)
      contextActions = [
        repositionAction,
        ...offenseActions.filter(a => ['movement', 'finishing'].includes(a.category)),
        ...defenseActions.filter(a => ['individual', 'pressing'].includes(a.category)),
      ];
    }
    return contextActions.slice(0, 8);
  }, [sportConfig, currentAgeGroup, currentPlay, currentStep]);

  // Get a player's current position (chains through all completed paths)
  const getPlayerCurrentPosition = useCallback((positionId) => {
    const pos = currentPlay?.positions?.find(p => p.id === positionId);
    if (!pos || pos.startX === null) return null;
    let cx = pos.startX;
    let cy = pos.startY;
    const playerPaths = (currentPlay.paths || [])
      .filter(p => p.positionId === positionId && p.points?.length >= 2)
      .sort((a, b) => (a.stepNumber || 1) - (b.stepNumber || 1));
    playerPaths.forEach(path => {
      const last = path.points[path.points.length - 1];
      if (last && last.x != null && last.y != null) {
        cx = last.x; cy = last.y;
      }
    });
    return { x: cx, y: cy };
  }, [currentPlay]);

  // Get next step number — always adds to the step you're viewing
  // Timeline boxes display 0-based, step numbers in data are same value
  // Box 0 = formation, Box 1 = step 1, etc.
  const getNextStepNumber = useCallback(() => {
    if (currentStep > 0) return currentStep;
    return 1;
  }, [currentStep]);

  // Save a completed path (click-to-place)
  const savePath = useCallback((endX, endY) => {
    if (!selectedPosition || !currentPlay) return;
    const startPos = getPlayerCurrentPosition(selectedPosition);
    if (!startPos) return;
    pushUndo(currentPlay);
    const pathType = currentDrawAction?.id || 'movement';
    const stepNumber = getNextStepNumber();
    const newPath = {
      id: `path-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      positionId: selectedPosition,
      type: pathType === 'move' ? 'movement' : pathType,
      points: [startPos, { x: endX, y: endY }],
      lineStyle: currentDrawAction?.lineStyle || 'dashed',
      stepNumber,
      hasBall: pathType === 'dribble',
      color: currentPlay.positions.find(p => p.id === selectedPosition)?.color || '#3b82f6'
    };
    handlePlayUpdate({
      ...currentPlay,
      paths: [...(currentPlay.paths || []), newPath]
    });
    // Stay on current step — don't jump
    setIsDrawing(false);
    setDrawPoints([]);
    setCurrentDrawAction(null);
  }, [selectedPosition, currentPlay, currentDrawAction, getPlayerCurrentPosition, getNextStepNumber, pushUndo, handlePlayUpdate]);

  // Save a pass (click on player)
  const savePass = useCallback((toPositionId) => {
    if (!selectedPosition || !currentPlay) return;
    pushUndo(currentPlay);
    const stepNumber = getNextStepNumber();
    const passType = currentDrawAction?.id === 'lob' || currentDrawAction?.id === 'cross' ? 'lob' : 'direct';
    const fromPos = getPlayerCurrentPosition(selectedPosition);
    const toPos = getPlayerCurrentPosition(toPositionId);
    const newPass = {
      id: `pass-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      fromPositionId: selectedPosition,
      toPositionId: toPositionId,
      passType,
      type: passType,
      stepNumber,
      lineStyle: currentDrawAction?.lineStyle || 'arrow',
      from: fromPos,
      to: toPos,
    };
    handlePlayUpdate({
      ...currentPlay,
      passes: [...(currentPlay.passes || []), newPass]
    });
    // Stay on current step — don't jump
    setIsDrawing(false);
    setDrawPoints([]);
    setCurrentDrawAction(null);
  }, [selectedPosition, currentPlay, currentDrawAction, getPlayerCurrentPosition, getNextStepNumber, pushUndo, handlePlayUpdate]);

  const handleSurfaceClick = useCallback((e) => {
    if (!currentPlay) return;

    const svg = surfaceRef.current?.querySelector('svg');
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width);
    const yPct = ((e.clientY - rect.top) / rect.height);
    const vbX = xPct * aspectEarly.w;
    const vbY = yPct * aspectEarly.h;

    // Check if clicked on a player — use animated position for hit detection
    const allPaths = currentPlay.paths || [];
    const allPositions = currentPlay.positions || [];
    const clickedPos = allPositions.find(pos => {
      const animPos = getAnimatedPosition(pos, currentStep, isPlaying, animationProgress, allPositions, allPaths);
      if (!animPos) return false;
      const dx = animPos.x - vbX;
      const dy = (animPos.y * scaleYEarly) - vbY;
      // Hit radius scales with sprite size
      const hitRadius = Math.max(5, spriteSize / 3);
      return Math.sqrt(dx * dx + dy * dy) < hitRadius;
    });

    // PENDING PASS TO SPACE: waiting for receiver click
    if (pendingPassToSpace && clickedPos && clickedPos.id !== pendingPassToSpace.fromPositionId) {
      const pp = pendingPassToSpace;
      pushUndo(currentPlay);
      // Create run path for receiver to the ball landing spot
      const receiverPos = getPlayerCurrentPosition(clickedPos.id);
      const runPath = receiverPos ? {
        id: `path-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        positionId: clickedPos.id,
        type: 'run',
        points: [receiverPos, { x: pp.targetX, y: pp.targetY }],
        lineStyle: 'dashed',
        stepNumber: pp.stepNumber,
        hasBall: false,
        color: clickedPos.color || '#3b82f6',
      } : null;

      const newPass = {
        id: `pass-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        fromPositionId: pp.fromPositionId,
        toPositionId: clickedPos.id,
        passType: pp.passType,
        type: pp.actionId,
        stepNumber: pp.stepNumber,
        lineStyle: pp.lineStyle,
        from: pp.fromPos,
        to: { x: pp.targetX, y: pp.targetY },
      };

      handlePlayUpdate({
        ...currentPlay,
        passes: [...(currentPlay.passes || []), newPass],
        paths: runPath ? [...(currentPlay.paths || []), runPath] : (currentPlay.paths || []),
      });
      setCurrentStep(pp.stepNumber);
      setPendingPassToSpace(null);
      return;
    }
    // Cancel pending pass if clicking empty space
    if (pendingPassToSpace && !clickedPos) {
      setPendingPassToSpace(null);
      return;
    }

    // CLICK-TO-PLACE: if we have a pending action, this click is the target
    if (isDrawing && currentDrawAction && selectedPosition && currentPlay) {
      const actionId = currentDrawAction.id;

      // PASS TO PLAYER: ball goes player-to-player, receiver gets possession
      const isDirectPass = ['pass', 'back-pass', 'switch-play', 'one-two',
        'flick', 'lay-off'].includes(actionId);
      if (isDirectPass) {
        if (clickedPos && clickedPos.id !== selectedPosition) {
          savePass(clickedPos.id);
        }
        // Must click a player for direct passes
        return;
      }

      // THROW-IN: click player = direct throw, click space = throw to space
      if (actionId === 'throw-in') {
        if (clickedPos && clickedPos.id !== selectedPosition) {
          savePass(clickedPos.id);
        } else if (!clickedPos) {
          // Throw to open space — treated as ball-to-location
          const scaleY = getScaleY(currentSport, currentPlay.courtView || surfaceViewEarly);
          const targetX = Math.round(vbX * 10) / 10;
          const targetY = Math.round((vbY / scaleY) * 10) / 10;
          const fromPos = getPlayerCurrentPosition(selectedPosition);
          if (fromPos) {
            pushUndo(currentPlay);
            const stepNumber = getNextStepNumber();
            const newPass = {
              id: `pass-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              fromPositionId: selectedPosition,
              toPositionId: null,
              passType: 'lob',
              type: 'throw-in',
              stepNumber,
              lineStyle: 'arrow',
              from: fromPos,
              to: { x: targetX, y: targetY },
            };
            handlePlayUpdate({
              ...currentPlay,
              passes: [...(currentPlay.passes || []), newPass]
            });
            // Stay on current step — don't jump
          }
          setIsDrawing(false);
          setDrawPoints([]);
          setCurrentDrawAction(null);
        }
        return;
      }

      // BALL TO LOCATION: shot, through ball, cross, lob, set pieces
      const isBallToLocation = ['shoot', 'through-ball', 'cross', 'lob',
        'goal-kick', 'corner-kick', 'free-kick'].includes(actionId);
      if (isBallToLocation) {
        const scaleY = getScaleY(currentSport, currentPlay.courtView || surfaceViewEarly);
        const targetX = Math.round(vbX * 10) / 10;
        const targetY = Math.round((vbY / scaleY) * 10) / 10;
        const fromPos = getPlayerCurrentPosition(selectedPosition);
        if (fromPos) {
          pushUndo(currentPlay);
          const stepNumber = getNextStepNumber();
          const passType = ['lob', 'cross', 'goal-kick', 'corner-kick'].includes(actionId) ? 'lob' : 'direct';

          // Through ball, cross, lob to space — ask who receives
          const needsReceiver = ['through-ball', 'cross', 'lob'].includes(actionId) && !clickedPos;
          let receiverId = clickedPos?.id || null;
          let runPath = null;

          if (needsReceiver) {
            // Store pending pass — next player click completes it
            setPendingPassToSpace({
              fromPositionId: selectedPosition,
              targetX, targetY,
              passType,
              actionId,
              stepNumber,
              lineStyle: currentDrawAction.lineStyle || 'arrow',
              fromPos,
            });
            setIsDrawing(false);
            setDrawPoints([]);
            setCurrentDrawAction(null);
            return;
          }

          const newPass = {
            id: `pass-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            fromPositionId: selectedPosition,
            toPositionId: receiverId,
            passType,
            type: actionId,
            stepNumber,
            lineStyle: currentDrawAction.lineStyle || 'arrow',
            from: fromPos,
            to: { x: targetX, y: targetY },
          };

          const updatedPlay = {
            ...currentPlay,
            passes: [...(currentPlay.passes || []), newPass],
            paths: runPath ? [...(currentPlay.paths || []), runPath] : (currentPlay.paths || []),
          };
          handlePlayUpdate(updatedPlay);
          // Stay on current step — don't jump
        }
        setIsDrawing(false);
        setDrawPoints([]);
        setCurrentDrawAction(null);
        return;
      }

      // PLAYER MOVEMENT: run, sprint, dribble, etc. Player moves to clicked spot.
      const scaleY = getScaleY(currentSport, currentPlay.courtView || surfaceViewEarly);
      savePath(Math.round(vbX * 10) / 10, Math.round((vbY / scaleY) * 10) / 10);
      return;
    }

    // Move tool: click is a no-op (drag handles movement)
    if (activeTool === 'move') {
      if (clickedPos) {
        setSelectedPosition(clickedPos.id);
        setWheelVisible(false);
      }
      return;
    }

    if (clickedPos) {
      if (activeTool === 'eraser') return;

      const animPos = getAnimatedPosition(clickedPos, currentStep, isPlaying, animationProgress, allPositions, allPaths);
      setSelectedPosition(clickedPos.id);
      setWheelPosition({
        x: (animPos.x / aspectEarly.w) * 100,
        y: ((animPos.y * scaleYEarly) / aspectEarly.h) * 100
      });
      setWheelActions(getActionsForPosition(clickedPos));
      setWheelColor(clickedPos.color || '#3b82f6');
      setWheelPlayerLabel(clickedPos.positionLabel || '');
      setWheelVisible(true);
      return;
    }

    // Check if clicked on a defender
    if (showDefenders) {
      const clickedDef = (currentPlay.defenders || []).find(def => {
        const defPos = getDefenderPosition(def, currentPlay.defenderPaths || [], currentStep, isPlaying, animationProgress);
        const dx = defPos.x - vbX;
        const dy = (defPos.y * scaleYEarly) - vbY;
        return Math.sqrt(dx * dx + dy * dy) < 4;
      });
      if (clickedDef) {
        setSelectedDefender(clickedDef.id);
        setWheelVisible(false);
        return;
      }
    }

    // Clicked empty space — dismiss wheel only (keep selection for facing/speed controls)
    setWheelVisible(false);
  }, [currentPlay, activeTool, showDefenders, getActionsForPosition, aspectEarly, scaleYEarly, currentSport, currentStep, isPlaying, animationProgress, selectedPosition, pushUndo, handlePlayUpdate, surfaceViewEarly, isDrawing, currentDrawAction, savePath, savePass, pendingPassToSpace, getPlayerCurrentPosition]);

  // Handle action selected from wheel
  const handleActionSelect = useCallback((action) => {
    setWheelVisible(false);

    // Reposition: switch to move tool so user can drag the player
    if (action.id === '_reposition') {
      setActiveTool('move');
      return;
    }

    // Assign: open roster picker for this position
    if (action.id === '_assign') {
      setRosterAssignTarget(selectedPosition);
      setShowRosterPicker(true);
      return;
    }

    setCurrentDrawAction(action);
    setIsDrawing(true);
    setDrawPoints([]);  // No start point needed — savePath reads current position
  }, [currentPlay, selectedPosition, scaleYEarly, currentStep, isPlaying, animationProgress]);

  // Mouse move: dragging players, defenders, or ball
  const handleSurfaceMouseMove = useCallback((e) => {
    if ((!draggingPlayer && !draggingDefender && !draggingBall) || !currentPlay) return;
    const svg = surfaceRef.current?.querySelector('svg');
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * aspectEarly.w;
    const y = ((e.clientY - rect.top) / rect.height) * aspectEarly.h;
    const scaleY = getScaleY(currentSport, currentPlay.courtView || surfaceViewEarly);
    const dataX = Math.round(x * 10) / 10;
    const dataY = Math.round((y / scaleY) * 10) / 10;

    if (draggingPlayer) {
      setCurrentPlay(prev => ({
        ...prev,
        positions: prev.positions.map(p =>
          p.id === draggingPlayer ? { ...p, startX: dataX, startY: dataY } : p
        )
      }));
    } else if (draggingDefender) {
      setCurrentPlay(prev => ({
        ...prev,
        defenders: (prev.defenders || []).map(d =>
          d.id === draggingDefender ? { ...d, x: dataX, y: dataY } : d
        )
      }));
    } else if (draggingBall) {
      // Move ball to nearest player or open position
      setCurrentPlay(prev => ({
        ...prev,
        ballPosition: { x: dataX, y: dataY },
      }));
    }
  }, [currentPlay, aspectEarly, draggingPlayer, draggingDefender, draggingBall, currentSport, surfaceViewEarly]);

  const handleSurfaceMouseUp = useCallback(() => {
    if (draggingPlayer && currentPlay) {
      setDraggingPlayer(null);
      setPlays(prev => prev.map(p => p.id === currentPlay.id ? currentPlay : p));
      savePlayForSport(currentPlay, currentSport);
    }
    if (draggingDefender && currentPlay) {
      setDraggingDefender(null);
      setPlays(prev => prev.map(p => p.id === currentPlay.id ? currentPlay : p));
      savePlayForSport(currentPlay, currentSport);
    }
    if (draggingBall && currentPlay) {
      setDraggingBall(false);
      // Snap ball to nearest player and give them possession
      const ballPos = currentPlay.ballPosition;
      if (ballPos) {
        let closest = null;
        let closestDist = Infinity;
        (currentPlay.positions || []).forEach(p => {
          const dx = p.startX - ballPos.x;
          const dy = p.startY - ballPos.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < closestDist) { closest = p; closestDist = dist; }
        });
        if (closest && closestDist < 10) {
          // Snap to player — give them possession
          const updated = {
            ...currentPlay,
            ballPosition: null,
            positions: currentPlay.positions.map(p => ({ ...p, hasBall: p.id === closest.id })),
          };
          handlePlayUpdate(updated);
        } else {
          // Ball in open space — keep it there
          handlePlayUpdate(currentPlay);
        }
      }
    }
  }, [draggingPlayer, draggingDefender, draggingBall, currentPlay, currentSport, handlePlayUpdate]);

  // Player drag
  const handlePlayerDrag = useCallback((positionId, newX, newY) => {
    if (!currentPlay) return;
    pushUndo(currentPlay);
    const updatedPlay = {
      ...currentPlay,
      positions: currentPlay.positions.map(p =>
        p.id === positionId ? { ...p, startX: newX, startY: newY } : p
      )
    };
    handlePlayUpdate(updatedPlay);
  }, [currentPlay, pushUndo, handlePlayUpdate]);

  // Formation apply
  const handleFormationApply = useCallback((formation) => {
    if (!currentPlay || !formation?.positions) return;
    pushUndo(currentPlay);

    const formationKeys = Object.keys(formation.positions);
    const updatedPositions = currentPlay.positions.map((pos, idx) => {
      const key = formationKeys[idx];
      const coords = key ? formation.positions[key] : null;
      return coords ? { ...pos, startX: coords.x, startY: coords.y } : pos;
    });

    handlePlayUpdate({ ...currentPlay, positions: updatedPositions });
  }, [currentPlay, pushUndo, handlePlayUpdate]);

  // ============================================
  // ANIMATION / PLAYBACK
  // ============================================

  useEffect(() => {
    if (!isPlaying) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      return;
    }

    let lastTime = performance.now();
    const stepDuration = 1000 / playbackSpeed;

    const animate = (now) => {
      const delta = now - lastTime;
      const progressIncrement = delta / stepDuration;

      setAnimationProgress(prev => {
        const next = prev + progressIncrement;
        if (next >= 1) {
          setCurrentStep(step => {
            const totalSteps = getMaxStep();
            if (step >= totalSteps) {
              setIsPlaying(false);
              return 0;
            }
            return step + 1;
          });
          return 0;
        }
        return next;
      });

      lastTime = now;
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [isPlaying, playbackSpeed]);

  const getMaxStep = useCallback(() => {
    if (!currentPlay) return 0;
    const pathSteps = (currentPlay.paths || []).map(p => p.stepNumber || 0);
    const passSteps = (currentPlay.passes || []).map(p => p.stepNumber || 0);
    const screenSteps = (currentPlay.screens || []).map(s => s.stepNumber || 0);
    const shotSteps = (currentPlay.shots || []).map(s => s.stepNumber || 0);
    const defPathSteps = (currentPlay.defenderPaths || []).map(p => p.stepNumber || 0);
    return Math.max(0, ...pathSteps, ...passSteps, ...screenSteps, ...shotSteps, ...defPathSteps);
  }, [currentPlay]);

  const handleAddStep = useCallback(() => {
    setCurrentStep(getMaxStep() + 1);
  }, [getMaxStep]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const positions = currentPlay?.positions || [];
  const paths = currentPlay?.paths || [];
  const passes = currentPlay?.passes || [];
  const screens = currentPlay?.screens || [];
  const shots = currentPlay?.shots || [];
  const defenders = currentPlay?.defenders || [];
  const defenderPaths = currentPlay?.defenderPaths || [];

  const currentBallHolder = positions.length > 0
    ? getBallHolderAtStep(currentStep, positions, paths, passes)
    : null;

  // Ball position for facing calculation — either placed on field or held by a player
  const ballWorldPos = useMemo(() => {
    if (currentPlay?.ballPosition) return currentPlay.ballPosition;
    if (currentBallHolder) {
      const holderPos = positions.find(p => p.id === currentBallHolder);
      if (holderPos) {
        const animPos = getAnimatedPosition(holderPos, currentStep, isPlaying, animationProgress, positions, paths);
        if (animPos) return animPos;
      }
    }
    return null;
  }, [currentPlay?.ballPosition, currentBallHolder, positions, paths, currentStep, isPlaying, animationProgress]);

  // ============================================
  // Convert an image URL to an inline data URL (blob SVGs can't load external resources)
  const urlToDataUrl = async (url) => {
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch {
      return url; // fallback to original
    }
  };

  // Capture empty field as PNG for GIF background
  // Hides overlays on the live SVG, rasterizes directly (textures already loaded by browser), restores
  const captureFieldBackground = useCallback(async () => {
    const svg = surfaceRef.current?.querySelector('svg');
    if (!svg) return null;

    // Temporarily hide players, defenders, paths, passes — keep only the surface
    const toHide = svg.querySelectorAll('image, [data-player-hit], [data-defender-hit], path[stroke-dasharray], line[stroke-dasharray]');
    const saved = [];
    toHide.forEach(el => {
      const href = el.getAttribute?.('href') || '';
      // Keep field texture images (grass, hardwood, etc.)
      if (el.tagName === 'image' && href.includes('/textures/')) return;
      saved.push({ el, display: el.style.display });
      el.style.display = 'none';
    });

    const scale = 2;
    const bbox = svg.getBoundingClientRect();
    const w = Math.round(bbox.width * scale);
    const h = Math.round(bbox.height * scale);

    // Rasterize the live SVG (browser already loaded all textures)
    const svgClone = svg.cloneNode(true);
    // Remove non-texture elements from clone (players, defenders, hit areas)
    svgClone.querySelectorAll('[data-player-hit], [data-defender-hit]').forEach(el => el.remove());
    svgClone.querySelectorAll('image').forEach(el => {
      const href = el.getAttribute?.('href') || '';
      // Keep texture images, remove everything else (sprites, ball)
      if (!href.includes('/textures/')) el.remove();
    });

    // Inline all texture images as data URLs for serialization
    const cloneImages = svgClone.querySelectorAll('image');
    for (const el of cloneImages) {
      const href = el.getAttribute('href') || '';
      if (href && !href.startsWith('data:')) {
        const fullUrl = href.startsWith('/') ? `${window.location.origin}${href}` : href;
        const dataUrl = await urlToDataUrl(fullUrl);
        el.setAttribute('href', dataUrl);
      }
    }

    svgClone.setAttribute('width', w);
    svgClone.setAttribute('height', h);

    // Ensure xmlns is set for standalone SVG serialization
    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

    const svgData = new XMLSerializer().serializeToString(svgClone);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    const img = new Image();
    let imgLoaded = false;
    await new Promise(r => {
      img.onload = () => { imgLoaded = true; r(); };
      img.onerror = () => { console.warn('[GIF] Field SVG failed to render'); r(); };
      img.src = svgUrl;
    });
    URL.revokeObjectURL(svgUrl);

    // Restore hidden elements
    saved.forEach(({ el, display }) => { el.style.display = display; });

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (imgLoaded) {
      ctx.drawImage(img, 0, 0, w, h);
    } else {
      // Fallback: green field
      ctx.fillStyle = '#2d5a27';
      ctx.fillRect(0, 0, w, h);
    }

    const fieldImg = new Image();
    await new Promise(r => { fieldImg.onload = r; fieldImg.src = canvas.toDataURL('image/png'); });
    return { fieldImg, w, h };
  }, []);

  // GIF EXPORT
  const handleExportGif = useCallback(async () => {
    if (exportInProgressRef.current || !currentPlay || !surfaceRef.current) return;
    exportInProgressRef.current = true;

    const maxStep = getMaxStep();
    if (maxStep === 0) {
      exportInProgressRef.current = false;
      return;
    }

    const savedStep = currentStep;
    const savedPlaying = isPlaying;
    const savedProgress = animationProgress;

    setIsExporting(true);
    setExportProgress(0);

    try {
      const svg = surfaceRef.current.querySelector('svg');
      if (!svg) throw new Error('SVG not found');

      const scale = 2;
      const bbox = svg.getBoundingClientRect();
      const width = Math.round(bbox.width * scale);
      const height = Math.round(bbox.height * scale);

      const framesPerStep = 25;
      const frameDelay = 50;
      const holdDelay = 500;
      const holdFirst = 1200;
      const holdLast = 1800;

      const gif = new GIF({
        workers: 2, quality: 8, width, height,
        workerScript: '/gif.worker.js'
      });

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      const totalFrames = 1 + (maxStep * framesPerStep);
      let frameCount = 0;

      // Capture the empty field as a static background
      console.log('[GIF] Capturing field background...');
      const fieldResult = await captureFieldBackground();
      if (!fieldResult) throw new Error('Failed to capture field');
      const { fieldImg } = fieldResult;

      // Pre-load all sprite images
      const spriteCache = {};
      const loadSprite = async (url) => {
        if (spriteCache[url]) return spriteCache[url];
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise(r => { img.onload = r; img.onerror = r; img.src = url; });
        spriteCache[url] = img;
        return img;
      };

      console.log('[GIF] Pre-loading sprites...');
      const dirs = ['N','NE','E','SE','S','SW','W','NW'];
      const combosToLoad = new Set();
      currentPlay.positions.forEach(p => {
        const j = p.jerseyColorPreset || 'blue';
        const s = p.shortsColorPreset || 'white';
        dirs.forEach(d => combosToLoad.add(`/sprites/color/${j}_${s}/player_${d}.png`));
      });
      if (currentPlay.defenders) {
        const dj = currentPlay.defJerseyPreset || 'red';
        const ds = currentPlay.defShortsPreset || 'black';
        const dgj = currentPlay.defGkJerseyPreset || 'yellow';
        const dgs = currentPlay.defGkShortsPreset || 'black';
        dirs.forEach(d => {
          combosToLoad.add(`/sprites/color/${dj}_${ds}/player_${d}.png`);
          combosToLoad.add(`/sprites/color/${dgj}_${dgs}/player_${d}.png`);
        });
      }
      combosToLoad.add('/sprites/soccer_ball.png');
      for (const url of combosToLoad) await loadSprite(url);
      console.log(`[GIF] Pre-loaded ${Object.keys(spriteCache).length} sprites`);

      // ViewBox scaling
      const vb = svg.viewBox.baseVal;
      const scX = width / vb.width;
      const scYFactor = height / vb.height;
      const sSize = spriteSize * scX;
      const ballSize = sSize * 0.35;

      const captureCurrentFrame = async (frameStep, frameProgress) => {
        const frameIsPlaying = frameStep > 0;

        // 1. Draw field background (green fallback if capture failed)
        ctx.clearRect(0, 0, width, height);
        if (fieldImg?.complete && fieldImg.naturalWidth > 0) {
          ctx.drawImage(fieldImg, 0, 0, width, height);
        } else {
          ctx.fillStyle = '#2d5a27';
          ctx.fillRect(0, 0, width, height);
        }

        // 2. Draw paths/passes (serialize SVG without images)
        if (showRoutes) {
          const svgClone = svg.cloneNode(true);
          svgClone.setAttribute('width', width);
          svgClone.setAttribute('height', height);
          svgClone.querySelectorAll('image').forEach(el => el.remove());
          svgClone.querySelectorAll('rect[data-player-hit], rect[data-defender-hit]').forEach(el => el.remove());
          // Remove the field surface background (first large rect/group)
          const bgRect = svgClone.querySelector('rect[width="100"]');
          if (bgRect) bgRect.setAttribute('fill', 'transparent');

          const svgData = new XMLSerializer().serializeToString(svgClone);
          const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
          const svgUrl = URL.createObjectURL(svgBlob);
          const svgImg = new Image();
          await new Promise(r => { svgImg.onload = r; svgImg.onerror = r; svgImg.src = svgUrl; });
          ctx.drawImage(svgImg, 0, 0, width, height);
          URL.revokeObjectURL(svgUrl);
        }

        // 3. Compute ball position for facing
        const frameBallHolderId = getBallHolderAtStep(frameStep, currentPlay.positions, currentPlay.paths || [], currentPlay.passes || []);
        let frameBallPos = currentPlay.ballPosition || null;
        if (!frameBallPos && frameBallHolderId) {
          const holder = currentPlay.positions.find(p => p.id === frameBallHolderId);
          if (holder) frameBallPos = getAnimatedPosition(holder, frameStep, frameIsPlaying, frameProgress, currentPlay.positions, currentPlay.paths || []);
        }

        // 4. Draw players
        for (const pos of currentPlay.positions) {
          const animPos = getAnimatedPosition(pos, frameStep, frameIsPlaying, frameProgress, currentPlay.positions, currentPlay.paths || []);
          if (!animPos || animPos.x == null) continue;

          const facing = getFacingDirection(pos.id, frameStep, currentPlay.positions, currentPlay.paths || [], frameProgress, frameBallPos, currentPlay.passes || []);
          const j = pos.jerseyColorPreset || 'blue';
          const s = pos.shortsColorPreset || 'white';
          const posMoving = frameIsPlaying && isPlayerMoving(pos.id, frameStep, currentPlay.paths || [], frameProgress);
          const runFrame = posMoving ? String(getRunFrame(frameProgress)).padStart(2, '0') : null;
          const spriteKey = posMoving
            ? `/sprites/color/${j}_${s}/player_run_${facing}_${runFrame}.png`
            : `/sprites/color/${j}_${s}/player_${facing}.png`;
          const sprite = spriteCache[spriteKey];

          const px = animPos.x * scX;
          const py = animPos.y * scaleYEarly * scYFactor;

          if (sprite?.complete && sprite.naturalWidth > 0) {
            ctx.drawImage(sprite, px - sSize / 2, py - sSize / 2, sSize, sSize);
          }

          // Name
          if (pos.playerName) {
            ctx.font = `500 ${11 * scale}px 'IBM Plex Sans', sans-serif`;
            ctx.fillStyle = 'rgba(255,255,255,0.8)';
            ctx.textAlign = 'center';
            ctx.fillText(pos.playerName.split(' ')[0], px, py - sSize * 0.35);
          }
        }

        // 4. Draw defenders
        if (showDefenders && currentPlay.defenders) {
          for (const def of currentPlay.defenders) {
            const defPos = getDefenderPosition(def, currentPlay.defenderPaths || [], frameStep, frameIsPlaying, frameProgress);
            const facing = def.facingDirection || 'S';
            const isGK = def.positionLabel === 'GK';
            const j = def.jerseyColorPreset || (isGK ? (currentPlay.defGkJerseyPreset || 'yellow') : (currentPlay.defJerseyPreset || 'red'));
            const s = def.shortsColorPreset || (isGK ? (currentPlay.defGkShortsPreset || 'black') : (currentPlay.defShortsPreset || 'black'));
            const sprite = spriteCache[`/sprites/color/${j}_${s}/player_${facing}.png`];

            const px = defPos.x * scX;
            const py = defPos.y * scaleYEarly * scYFactor;

            if (sprite?.complete && sprite.naturalWidth > 0) {
              ctx.drawImage(sprite, px - sSize / 2, py - sSize / 2, sSize, sSize);
            }
          }
        }

        // 5. Draw ball — match on-screen facing-based offset
        const ballHolderId = getBallHolderAtStep(frameStep, currentPlay.positions, currentPlay.paths || [], currentPlay.passes || []);
        const ballSprite = spriteCache['/sprites/soccer_ball.png'];
        if (ballHolderId && ballSprite?.complete) {
          const holder = currentPlay.positions.find(p => p.id === ballHolderId);
          if (holder) {
            const animPos = getAnimatedPosition(holder, frameStep, frameIsPlaying, frameProgress, currentPlay.positions, currentPlay.paths || []);
            if (animPos) {
              // Get movement direction for ball offset (same as on-screen)
              const holderPaths = (currentPlay.paths || []).filter(p => p.positionId === holder.id && p.points?.length >= 2)
                .sort((a, b) => (a.stepNumber || 1) - (b.stepNumber || 1));
              const lastHolderPath = holderPaths.filter(p => (p.stepNumber || 1) <= frameStep).pop();
              let ballFacing = 'S';
              if (lastHolderPath) {
                const pts = lastHolderPath.points;
                const last = pts[pts.length - 1];
                const prev = pts[pts.length - 2];
                if (last && prev) {
                  const ddx = last.x - prev.x;
                  const ddy = last.y - prev.y;
                  if (Math.abs(ddx) > 0.3 || Math.abs(ddy) > 0.3) {
                    ballFacing = angleToBearing(ddx, ddy);
                  }
                }
              }
              const bHalf = sSize / 2;
              const bR = bHalf * 0.4;
              const bOffsets = {
                N:  { x: 0,         y: -bR },
                NE: { x: bR * 0.7,  y: -bR * 0.7 },
                E:  { x: bR,        y: 0 },
                SE: { x: bR * 0.7,  y: bR * 0.7 },
                S:  { x: 0,         y: bR },
                SW: { x: -bR * 0.7, y: bR * 0.7 },
                W:  { x: -bR,       y: 0 },
                NW: { x: -bR * 0.7, y: -bR * 0.7 },
              };
              const bOff = bOffsets[ballFacing] || bOffsets.S;
              const px = animPos.x * scX;
              const py = animPos.y * scaleYEarly * scYFactor;
              ctx.drawImage(ballSprite, px + bOff.x - ballSize / 2, py + bOff.y - ballSize / 2, ballSize, ballSize);
            }
          }
        }
        // Ball position if placed on field
        if (currentPlay.ballPosition && ballSprite?.complete) {
          const bx = currentPlay.ballPosition.x * scX;
          const by = currentPlay.ballPosition.y * scaleYEarly * scYFactor;
          ctx.drawImage(ballSprite, bx - ballSize / 2, by - ballSize / 2, ballSize, ballSize);
        }

        // 6. Step label
        ctx.font = `bold ${18 * scale}px 'IBM Plex Sans', sans-serif`;
        ctx.textAlign = 'left';
        ctx.fillStyle = 'rgba(15,23,42,0.85)';
        ctx.beginPath();
        ctx.roundRect(10 * scale, 10 * scale, 130 * scale, 28 * scale, 6 * scale);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.fillText(`Step ${frameStep} / ${maxStep}`, 18 * scale, 30 * scale);
      };

      // Step 0 — starting positions
      flushSync(() => { setCurrentStep(0); setIsPlaying(false); setAnimationProgress(0); });
      await new Promise(r => setTimeout(r, 100));
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      await captureCurrentFrame(0, 0);
      gif.addFrame(ctx, { copy: true, delay: holdFirst });
      frameCount++;
      setExportProgress(Math.round((frameCount / totalFrames) * 90));

      // Each step with animation
      for (let step = 1; step <= maxStep; step++) {
        for (let frame = 0; frame < framesPerStep; frame++) {
          const progress = frame / (framesPerStep - 1);
          flushSync(() => { setCurrentStep(step); setIsPlaying(true); setAnimationProgress(progress); });
          await new Promise(r => setTimeout(r, 60));
          await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
          await captureCurrentFrame(step, progress);

          let delay = frameDelay;
          if (frame === framesPerStep - 1 && step === maxStep) delay = holdLast;
          else if (frame === framesPerStep - 1) delay = holdDelay;

          gif.addFrame(ctx, { copy: true, delay });
          frameCount++;
          setExportProgress(Math.round((frameCount / totalFrames) * 90));
        }
      }

      setExportProgress(92);

      gif.on('finished', (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(currentPlay.name || 'play').replace(/\s+/g, '-')}.gif`;
        a.click();
        URL.revokeObjectURL(url);

        flushSync(() => { setCurrentStep(savedStep); setIsPlaying(savedPlaying); setAnimationProgress(savedProgress); });
        setIsExporting(false);
        setExportProgress(0);
        exportInProgressRef.current = false;
      });

      gif.on('progress', (p) => setExportProgress(92 + Math.round(p * 8)));
      gif.render();

    } catch (error) {
      console.error('GIF export error:', error);
      flushSync(() => { setCurrentStep(savedStep); setIsPlaying(savedPlaying); setAnimationProgress(savedProgress); });
      setIsExporting(false);
      setExportProgress(0);
      exportInProgressRef.current = false;
    }
  }, [currentPlay, currentStep, isPlaying, animationProgress]);

  // SVG RENDERING FUNCTIONS
  // ============================================

  const renderPaths = (scaleY) => {
    if (!showRoutes) return null;
    return paths.map((path) => {
      const stepNum = path.stepNumber || 0;

      // Step visibility
      let pathOpacity = 0.85;
      let showPath = true;

      if (currentStep === 0) {
        showPath = false;
      } else if (stepNum < currentStep) {
        pathOpacity = 0.3;
      } else if (stepNum === currentStep) {
        pathOpacity = 1.0;
      } else {
        showPath = false;
      }

      if (!showPath) return null;

      const player = positions.find(p => p.id === path.positionId);
      const playerColor = path.color || player?.color || '#3b82f6';
      const pathType = path.type || 'movement';
      const startPoint = path.points[0];

      // Scale points from data space to viewBox space for rendering
      const scaledPoints = path.points.map(p => ({ x: p.x, y: p.y * scaleY }));
      const offsetStart = getOffsetStartPoint(scaledPoints, GHOST_RADIUS);
      const offsetPoints = [offsetStart, ...scaledPoints.slice(1)];
      const d = smoothPath(offsetPoints);

      // Chevrons
      const chevronPositions = getChevronPositions(offsetPoints);
      const chevronCount = chevronPositions.length;

      // Step badge
      const badge = offsetPoints.length >= 2 ? getStepBadgePosition(offsetPoints) : null;

      return (
        <g key={path.id} opacity={pathOpacity}>
          {/* Ghost circle at start */}
          <circle
            cx={startPoint.x} cy={startPoint.y * scaleY}
            r={GHOST_RADIUS} fill="none" stroke={playerColor}
            strokeWidth="0.5" strokeDasharray="2,1" opacity="0.5"
          />
          <text
            x={startPoint.x} y={startPoint.y * scaleY + 0.3}
            textAnchor="middle" dominantBaseline="middle"
            fill={playerColor} fontSize="2.2" fontWeight="bold" opacity="0.4"
          >{player?.positionLabel || ''}</text>

          {/* White outline for visibility */}
          <path d={d} fill="none" stroke="#fff" strokeWidth="1.4"
            strokeLinecap="round" strokeLinejoin="round" opacity="0.5"
          />

          {/* Main colored path */}
          <path d={d} fill="none" stroke={playerColor} strokeWidth="0.6"
            strokeLinecap="round" strokeLinejoin="round"
            strokeDasharray={pathType === 'movement' || path.lineStyle === 'dashed' ? '2,1.5' : 'none'}
            opacity="0.85"
          />

          {/* Chevrons by type */}
          {pathType === 'dribble' && chevronPositions.map((pt, idx) => (
            <g key={`drib-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`}>
              <BallImage x={pt.x} y={pt.y} size={BALL_SIZE} />
            </g>
          ))}

          {pathType === 'cut' && chevronPositions.map((pt, idx) => (
            <g key={`cut-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`}
              transform={`translate(${pt.x}, ${pt.y}) rotate(${pt.angle})`}>
              <path d="M-1.8,-1.2 L-0.6,0 L-1.8,1.2" fill="none" stroke={playerColor}
                strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M-0.6,-1.2 L0.6,0 L-0.6,1.2" fill="none" stroke={playerColor}
                strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
          ))}

          {pathType === 'movement' && chevronPositions.map((pt, idx) => (
            <g key={`chev-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`}
              transform={`translate(${pt.x}, ${pt.y}) rotate(${pt.angle})`}>
              <path d="M-1.2,-1 L0,0 L-1.2,1" fill="none" stroke={playerColor}
                strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
          ))}

          {/* Step badge */}
          {badge && (
            <g>
              <circle cx={badge.x} cy={badge.y} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
              <text x={badge.x} y={badge.y + 0.3} textAnchor="middle" dominantBaseline="middle"
                fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
            </g>
          )}
        </g>
      );
    });
  };

  const renderPasses = (scaleY) => {
    return passes.map((pass) => {
      const stepNum = pass.stepNumber || 1;

      let passOpacity = 0.85;
      let showPass = true;
      let showAnimatedBall = false;
      let ballProgress = 0;

      if (currentStep === 0) {
        showPass = false;
      } else if (stepNum < currentStep) {
        passOpacity = 0.3;
      } else if (stepNum === currentStep) {
        passOpacity = 1.0;
        // Ball rendering handled by phased inline block below
      } else {
        showPass = false;
      }

      if (!showPass) return null;

      const fromPos = getPlayerPositionAtStep(pass.fromPositionId, stepNum - 1, positions, paths) || pass.from;
      const toPos = getPlayerPositionAtStep(pass.toPositionId, stepNum - 1, positions, paths) || pass.to;
      const passer = positions.find(p => p.id === pass.fromPositionId);
      const passerColor = passer?.color || '#374151';

      // Scale Y coordinates
      const fromScaled = { x: fromPos.x, y: fromPos.y * scaleY };
      const toScaled = { x: toPos.x, y: toPos.y * scaleY };

      const d = pass.type === 'lob'
        ? `M ${fromScaled.x},${fromScaled.y} Q ${(fromScaled.x + toScaled.x) / 2},${Math.min(fromScaled.y, toScaled.y) - 8} ${toScaled.x},${toScaled.y}`
        : `M ${fromScaled.x},${fromScaled.y} L ${toScaled.x},${toScaled.y}`;

      return (
        <g key={pass.id}>
          {/* Pass lines and arrows — only when routes visible */}
          {showRoutes && (
            <g opacity={passOpacity}>
              {/* White outline */}
              <path d={d} fill="none" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
              {/* Pass line */}
              <path d={d} fill="none" stroke={passerColor} strokeWidth="0.6"
                strokeDasharray={pass.type === 'bounce' ? '2,2' : 'none'}
                strokeLinecap="round"
              />
              {/* Arrow head at end */}
              {(() => {
                const dx = toScaled.x - fromScaled.x;
                const dy = toScaled.y - fromScaled.y;
                const angle = Math.atan2(dy, dx) * (180 / Math.PI);
                return (
                  <g transform={`translate(${toScaled.x}, ${toScaled.y}) rotate(${angle})`}>
                    <path d="M-3,-1.5 L0,0 L-3,1.5" fill="none" stroke={passerColor}
                      strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </g>
                );
              })()}

              {/* Static ball indicator in design mode */}
              {!isPlaying && (() => {
                const midPoint = getPointOnLine(fromScaled, toScaled, 0.5);
                return (
                  <g transform={`translate(${midPoint.x}, ${midPoint.y})`}>
                    <text y="0.3" textAnchor="middle" dominantBaseline="middle"
                      fontSize="2.5" opacity="0.6">{getBallEmoji(currentSport)}</text>
                  </g>
                );
              })()}
            </g>
          )}

          {/* Ball during pass step — at feet, launches on contact, travels, lands at receiver */}
          {isPlaying && stepNum === currentStep && (() => {
            if (animationProgress < BALL_LAUNCH) {
              // Ball at passer's feet during wind-up
              return <BallImage x={fromScaled.x} y={fromScaled.y + 1} size={BALL_SIZE} />;
            }
            if (animationProgress < BALL_PHASE.end) {
              // Ball traveling from passer to receiver
              const bp = Math.min(1, (animationProgress - BALL_PHASE.start) / (BALL_PHASE.end - BALL_PHASE.start));
              const ballPos = getPassBallPosition(fromScaled, toScaled, bp, pass.type);
              return <BallImage x={ballPos.x} y={ballPos.y} size={BALL_SIZE} />;
            } else {
              // Ball arrived at receiver — show at receiver's feet
              return <BallImage x={toScaled.x} y={toScaled.y} size={BALL_SIZE} />;
            }
          })()}

          {/* Step badge — only when routes visible */}
          {showRoutes && (() => {
            const dx = toScaled.x - fromScaled.x;
            const dy = toScaled.y - fromScaled.y;
            const midX = fromScaled.x + dx * 0.4;
            const midY = fromScaled.y + dy * 0.4;
            const angle = Math.atan2(dy, dx);
            const perpAngle = angle + Math.PI / 2;
            const badgeX = midX + Math.cos(perpAngle) * 3;
            const badgeY = midY + Math.sin(perpAngle) * 3;
            return (
              <g>
                <circle cx={badgeX} cy={badgeY} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
                <text x={badgeX} y={badgeY + 0.3} textAnchor="middle" dominantBaseline="middle"
                  fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
              </g>
            );
          })()}
        </g>
      );
    });
  };

  const renderShots = (scaleY) => {
    return shots.map((shot, idx) => {
      const stepNum = shot.stepNumber || 1;
      const shotTarget = getShotTarget(currentSport);

      let shotOpacity = 0.9;
      let showShot = true;
      let showAnimatedBall = false;
      let ballProgress = 0;

      if (currentStep === 0) {
        showShot = false;
      } else if (stepNum < currentStep) {
        shotOpacity = 0.3;
      } else if (stepNum === currentStep) {
        shotOpacity = 1.0;
        if (isPlaying) {
          // Ball launches on foot contact, travels during BALL_PHASE
          if (animationProgress >= BALL_LAUNCH) {
            showAnimatedBall = true;
            ballProgress = Math.min(1, (animationProgress - BALL_PHASE.start) / (BALL_PHASE.end - BALL_PHASE.start));
          }
        }
      } else {
        showShot = false;
      }

      if (!showShot) return null;

      const shooterPos = getPlayerPositionAtStep(shot.shooterId, stepNum - 1, positions, paths);
      if (!shooterPos) return null;

      const shooterScaled = { x: shooterPos.x, y: shooterPos.y * scaleY };
      const targetScaled = { x: shotTarget.x, y: shotTarget.y * scaleY };

      return (
        <g key={shot.id || `shot-${idx}`} opacity={shotOpacity}>
          {/* Animated ball along arc */}
          {showAnimatedBall && (() => {
            const ballPos = getShotBallPosition(shooterScaled, targetScaled, ballProgress);
            return (
              <g transform={`translate(${ballPos.x}, ${ballPos.y})`}>
                <circle r={2.2 * ballPos.scale} fill="#ea580c" stroke="#fff" strokeWidth="0.3" />
                <text y="0.3" textAnchor="middle" dominantBaseline="middle"
                  fontSize={3.2 * ballPos.scale}
                  style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }}>
                  {getBallEmoji(currentSport)}
                </text>
              </g>
            );
          })()}

          {/* Static ball at target when not animating */}
          {!isPlaying && (
            <g transform={`translate(${targetScaled.x}, ${targetScaled.y - 2})`}>
              <circle r="1.8" fill="#ea580c" stroke="#fff" strokeWidth="0.2" opacity="0.8" />
              <text y="0.2" textAnchor="middle" dominantBaseline="middle"
                fontSize="2.5" opacity="0.9">{getBallEmoji(currentSport)}</text>
            </g>
          )}

          {/* Swish effect */}
          {showAnimatedBall && ballProgress > 0.85 && (
            <g transform={`translate(${targetScaled.x}, ${targetScaled.y})`}>
              <circle r={3 + (ballProgress - 0.85) * 20} fill="none"
                stroke="#f59e0b" strokeWidth="0.5"
                opacity={1 - (ballProgress - 0.85) * 6} />
            </g>
          )}

          {/* Step badge near shooter */}
          {(() => {
            const badgePos = getShotBallPosition(shooterScaled, targetScaled, 0.15);
            const dx = targetScaled.x - shooterScaled.x;
            const dy = targetScaled.y - shooterScaled.y;
            const angle = Math.atan2(dy, dx);
            const perpAngle = angle + Math.PI / 2;
            const badgeX = badgePos.x + Math.cos(perpAngle) * 3;
            const badgeY = badgePos.y + Math.sin(perpAngle) * 3;
            return (
              <g>
                <circle cx={badgeX} cy={badgeY} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
                <text x={badgeX} y={badgeY + 0.3} textAnchor="middle" dominantBaseline="middle"
                  fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
              </g>
            );
          })()}
        </g>
      );
    });
  };

  const renderScreens = (scaleY) => {
    return screens.map((screen, i) => {
      const setter = positions.find(p => p.id === screen.setterId);
      const setterColor = setter?.color || '#374151';
      const screenX = screen.x;
      const screenY = screen.y * scaleY;
      const rotation = screen.rotation || 0;
      const stepNum = screen.stepNumber || 1;

      const setterRollPath = paths.find(p =>
        p.positionId === screen.setterId &&
        (p.stepNumber || 1) > stepNum &&
        !p.isScreenMove
      );
      const rollStep = setterRollPath?.stepNumber || Infinity;

      const screenMovePath = paths.find(p =>
        p.positionId === screen.setterId &&
        p.isScreenMove &&
        (p.stepNumber || 1) === stepNum
      );

      let screenOpacity = 1;
      let showScreen = true;

      if (currentStep === 0) {
        showScreen = false;
      } else if (currentStep < stepNum) {
        showScreen = false;
      } else if (currentStep >= rollStep) {
        showScreen = false;
      } else if (isPlaying && currentStep === stepNum && screenMovePath) {
        if (animationProgress < 0.95) {
          showScreen = false;
        } else {
          screenOpacity = 0.5 + animationProgress * 0.5;
        }
      }

      if (!showScreen) return null;

      const widthMult = 1.2;
      const heightMult = 0.6;
      const barW = 10 * widthMult;
      const barH = 4 * heightMult;

      return (
        <g key={screen.id || `screen-${i}`} opacity={screenOpacity}>
          <g transform={`translate(${screenX}, ${screenY}) rotate(${rotation})`}>
            <rect
              x={-barW / 2} y={-barH / 2}
              width={barW} height={barH}
              rx={(screen.shape || 'sharp') === 'rounded' ? 1.5 : 0}
              fill={setterColor} stroke="#fff" strokeWidth="0.3"
            />
          </g>
          <circle cx={screenX} cy={screenY} r="2"
            fill={setterColor} stroke="#fff" strokeWidth="0.4" />
          <text x={screenX} y={screenY + 0.3}
            textAnchor="middle" dominantBaseline="middle"
            fill="#fff" fontSize="1.8" fontWeight="bold"
            style={{ pointerEvents: 'none' }}>
            {setter?.positionLabel || '?'}
          </text>
        </g>
      );
    });
  };

  const renderPlayers = (scaleY) => {
    return positions.map(pos => {
      const animPos = getAnimatedPosition(pos, currentStep, isPlaying, animationProgress, positions, paths);
      if (!animPos || animPos.x == null || animPos.y == null) return null;

      const playerX = animPos.x;
      const playerY = animPos.y * scaleY;
      const hasBall = pos.id === currentBallHolder;

      // Check if player is hidden by active screen bar
      const playerScreen = screens.find(s => s.setterId === pos.id);
      if (playerScreen) {
        const screenStep = playerScreen.stepNumber || 1;
        const rollPath = paths.find(p =>
          p.positionId === pos.id &&
          (p.stepNumber || 1) > screenStep &&
          !p.isScreenMove
        );
        const rollStep = rollPath?.stepNumber || Infinity;
        const screenIsVisible = isPlaying && (
          (currentStep === screenStep && animationProgress >= 0.95) ||
          (currentStep > screenStep && currentStep < rollStep)
        );
        if (screenIsVisible && selectedPosition !== pos.id) {
          return null; // Screen bar shows the player icon
        }
      }

      // Check if player has any actions
      const hasActions =
        paths.some(p => p.positionId === pos.id) ||
        passes.some(p => p.fromPositionId === pos.id || p.toPositionId === pos.id) ||
        screens.some(s => s.setterId === pos.id) ||
        shots.some(s => s.shooterId === pos.id);
      const playerOpacity = 1;

      // Ball offset calculated per-facing inside render

      const isMoving = activeTool === 'move' && selectedPosition === pos.id;
      const isDragging = draggingPlayer === pos.id;

      return (
        <g key={pos.id} opacity={activeTool === 'move' ? 1 : playerOpacity}>
          <g
            className={hasBall && activeTool !== 'move' ? 'ball-holder' : ''}
            style={{ cursor: activeTool === 'move' ? 'grab' : 'pointer', pointerEvents: 'auto' }}
            onMouseDown={activeTool === 'move' ? (e) => {
              e.stopPropagation();
              e.preventDefault();
              pushUndo(currentPlay);
              setDraggingPlayer(pos.id);
              setSelectedPosition(pos.id);
              setWheelVisible(false);
            } : undefined}
          >
            {/* Move tool: crosshair at exact position */}
            {activeTool === 'move' && (
              <>
                <line x1={playerX - 3.5} y1={playerY} x2={playerX + 3.5} y2={playerY}
                  stroke="rgba(255,255,0,0.3)" strokeWidth="0.12" style={{ pointerEvents: 'none' }} />
                <line x1={playerX} y1={playerY - 3.5} x2={playerX} y2={playerY + 3.5}
                  stroke="rgba(255,255,0,0.3)" strokeWidth="0.12" style={{ pointerEvents: 'none' }} />
              </>
            )}
            {/* Invisible hit area for click/drag — matches sprite size */}
            <rect
              data-player-hit={pos.id}
              x={playerX - spriteSize / 3}
              y={playerY - spriteSize / 3}
              width={spriteSize * 2 / 3}
              height={spriteSize * 2 / 3}
              fill="rgba(0,0,0,0.001)"
              style={{ cursor: activeTool === 'move' ? 'grab' : 'pointer' }}
            />
            {/* Player sprite — pre-colored from Blender */}
            {(() => {
              const facing = getFacingDirection(pos.id, currentStep, positions, paths, isPlaying ? animationProgress : null, ballWorldPos, passes);
              const half = spriteSize / 2;
              const sx = playerX - half;
              const sy = playerY - half - 1;
              const selected = selectedPosition === pos.id;
              const dropShadow = isDragging
                ? 'drop-shadow(0 0 4px rgba(251,191,36,0.8))'
                : selected ? `drop-shadow(0 0 2px ${pos.color})` : 'drop-shadow(0 0 1px rgba(0,0,0,0.5))';

              // Map position color to nearest preset
              const jerseyPreset = pos.jerseyColorPreset || 'blue';
              const shortsPreset = pos.shortsColorPreset || 'white';
              const comboDir = `${jerseyPreset}_${shortsPreset}`;
              const { action: playerAction, frameIdx } = isPlaying
                ? getPlayerAction(pos.id, currentStep, paths, passes, animationProgress)
                : { action: 'idle', frameIdx: null };
              // Only use animated sprites for actions that have been rendered
              const RENDERED_ACTIONS = ['run', 'kick', 'pass', 'receive'];
              const useAnimated = playerAction !== 'idle' && RENDERED_ACTIONS.includes(playerAction);
              const spriteUrl = useAnimated
                ? `/sprites/color/${comboDir}/player_${playerAction}_${facing}_${frameIdx}.png`
                : `/sprites/color/${comboDir}/player_${facing}.png`;

              return (
                <>
                  <image
                    href={spriteUrl}
                    x={sx} y={sy} width={spriteSize} height={spriteSize}
                    style={{ filter: dropShadow, pointerEvents: 'none' }}
                  />
                  {/* Selection ring */}
                  {(selected || isDragging) && (
                    <circle
                      cx={playerX} cy={playerY}
                      r={half - 0.5}
                      fill="none"
                      stroke={isDragging ? '#fbbf24' : '#ffffff'}
                      strokeWidth={0.4}
                      strokeDasharray="1,0.8"
                      opacity={0.7}
                    />
                  )}
                  {/* Jersey number on sprite — visible on back-facing angles */}
                  {showJerseyNumbers && ['N', 'NE', 'NW'].includes(facing) && (pos.jerseyNumber != null) && (
                    <text
                      x={playerX} y={playerY - 0.8}
                      textAnchor="middle" dominantBaseline="central"
                      fill="#ffffff" fontSize="1.8" fontWeight="bold"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      style={{ pointerEvents: 'none', textShadow: '0 0 2px rgba(0,0,0,0.8)' }}
                    >
                      {pos.jerseyNumber}
                    </text>
                  )}
                  {/* Ball in front of player's feet — hidden during pass/shot steps (ball shown by pass animation) */}
                  {hasBall && activeTool !== 'move' && !(isPlaying && (passes || []).some(p => (p.stepNumber || 1) === currentStep && (p.fromPositionId === pos.id || p.toPositionId === pos.id))) && (() => {
                    // For ball holder, use last path direction (not facing which may look at ball)
                    const playerPaths = (paths || []).filter(p => p.positionId === pos.id && p.points?.length >= 2)
                      .sort((a, b) => (a.stepNumber || 1) - (b.stepNumber || 1));
                    const lastPath = playerPaths.filter(p => (p.stepNumber || 1) <= (currentStep + 1)).pop();
                    let ballFacing = facing;
                    if (lastPath) {
                      const pts = lastPath.points;
                      const last = pts[pts.length - 1];
                      const prev = pts[pts.length - 2];
                      if (last && prev) {
                        const dx = last.x - prev.x;
                        const dy = last.y - prev.y;
                        if (Math.abs(dx) > 0.3 || Math.abs(dy) > 0.3) {
                          ballFacing = angleToBearing(dx, dy);
                        }
                      }
                    }
                    const r = half * 0.4;
                    const offsets = {
                      N:  { x: 0,        y: -r },
                      NE: { x: r * 0.7,  y: -r * 0.7 },
                      E:  { x: r,        y: 0 },
                      SE: { x: r * 0.7,  y: r * 0.7 },
                      S:  { x: 0,        y: r },
                      SW: { x: -r * 0.7, y: r * 0.7 },
                      W:  { x: -r,       y: 0 },
                      NW: { x: -r * 0.7, y: -r * 0.7 },
                    };
                    const off = offsets[ballFacing] || offsets.S;
                    return <BallImage x={playerX + off.x} y={playerY + off.y + 2} size={BALL_SIZE} />;
                  })()}
                </>
              );
            })()}
            {/* Player name above dot — rounded background for readability */}
            {pos.playerName && (() => {
              const name = pos.playerName.split(' ')[0];
              const nameY = playerY - spriteSize / 2 * 0.7;
              const charW = 0.85;
              const padX = 1.5;
              const bgW = name.length * charW + padX * 2;
              const bgH = 2.4;
              return (
                <g style={{ pointerEvents: 'none' }}>
                  <rect
                    x={playerX - bgW / 2} y={nameY - bgH / 2}
                    width={bgW} height={bgH}
                    rx={bgH / 2} ry={bgH / 2}
                    fill="rgba(15,23,42,0.7)"
                  />
                  <text
                    x={playerX} y={nameY}
                    textAnchor="middle" dominantBaseline="central"
                    fill="#ffffff" fontSize="1.4" fontWeight="600"
                    fontFamily="'IBM Plex Sans', sans-serif"
                  >
                    {name}
                  </text>
                </g>
              );
            })()}
            {/* Move tool: coordinate label below player */}
            {activeTool === 'move' && (
              <text
                x={playerX} y={playerY + 5}
                textAnchor="middle" dominantBaseline="middle"
                fill="#fbbf24" fontSize="1.4" fontWeight="bold"
                fontFamily="monospace" opacity="0.9"
                style={{ pointerEvents: 'none' }}
              >
                {pos.startX},{pos.startY}
              </text>
            )}
          </g>
        </g>
      );
    });
  };

  const renderDefenders = (scaleY) => {
    if (!showDefenders) return null;
    return defenders.map((def, idx) => {
      const defPos = getDefenderPosition(def, defenderPaths, currentStep, isPlaying, animationProgress);
      const defX = defPos.x;
      const defY = defPos.y * scaleY;

      const displayLabel = def.positionLabel || def.label || `D${idx + 1}`;

      // Defender facing: toward the offensive half (South)
      const defFacing = def.facingDirection || 'S';
      const isDefGK = def.positionLabel === 'GK' || def.label === 'GK';
      const defJersey = def.jerseyColorPreset || (isDefGK ? (currentPlay?.defGkJerseyPreset || 'yellow') : (currentPlay?.defJerseyPreset || 'red'));
      const defShorts = def.shortsColorPreset || (isDefGK ? (currentPlay?.defGkShortsPreset || 'black') : (currentPlay?.defShortsPreset || 'black'));
      const { action: defAction, frameIdx: defFrameIdx } = isPlaying
        ? getPlayerAction(def.id, currentStep, defenderPaths, [], animationProgress)
        : { action: 'idle', frameIdx: null };
      const RENDERED_ACTIONS_DEF = ['run'];
      const useDefAnimated = defAction !== 'idle' && RENDERED_ACTIONS_DEF.includes(defAction);
      const defSpriteUrl = useDefAnimated
        ? `/sprites/color/${defJersey}_${defShorts}/player_${defAction}_${defFacing}_${defFrameIdx}.png`
        : `/sprites/color/${defJersey}_${defShorts}/player_${defFacing}.png`;
      const half = spriteSize / 2;
      const isSelected = selectedDefender === def.id;

      return (
        <g key={def.id || `defender-${idx}`}
          style={{ cursor: activeTool === 'move' ? 'grab' : 'pointer', pointerEvents: 'auto' }}
          onClick={() => {
            if (activeTool !== 'move') {
              setSelectedDefender(def.id);
              setSelectedPosition(null);
              // Open action wheel for defender with defensive actions
              const defenseActions = sportConfig?.actions?.defense || [];
              const repositionAction = { id: '_reposition', name: 'Reposition', category: 'utility', icon: 'move' };
              const defActions = [
                repositionAction,
                ...defenseActions.filter(a => ['individual', 'pressing', 'team'].includes(a.category)),
              ].slice(0, 8);
              setWheelPosition({
                x: (defX / aspectEarly.w) * 100,
                y: (defY / aspectEarly.h) * 100,
              });
              setWheelActions(defActions);
              setWheelColor('#ef4444');
              setWheelPlayerLabel(displayLabel);
              setWheelVisible(true);
            }
          }}
          onMouseDown={activeTool === 'move' ? (e) => {
            e.stopPropagation();
            e.preventDefault();
            setDraggingDefender(def.id);
            setSelectedDefender(def.id);
            setSelectedPosition(null);
            setWheelVisible(false);
          } : undefined}>
          {/* Hit area */}
          <rect
            data-defender-hit={def.id}
            x={defX - half / 3} y={defY - half / 3}
            width={half * 2 / 3} height={half * 2 / 3}
            fill="rgba(0,0,0,0.001)"
          />
          {/* Sprite */}
          <image
            href={defSpriteUrl}
            x={defX - half} y={defY - half - 1}
            width={spriteSize} height={spriteSize}
            style={{
              filter: isSelected ? 'drop-shadow(0 0 3px rgba(251,191,36,0.8))' : 'drop-shadow(0 0 1px rgba(0,0,0,0.5))',
              pointerEvents: 'none',
            }}
          />
          {/* Selection ring */}
          {isSelected && (
            <circle cx={defX} cy={defY} r={half - 0.5}
              fill="none" stroke="#fbbf24" strokeWidth={0.4}
              strokeDasharray="1,0.8" opacity={0.7} />
          )}
          {/* Position label above */}
          <text x={defX} y={defY - half * 0.7}
            textAnchor="middle" dominantBaseline="middle"
            fill="rgba(255,200,200,0.8)" fontSize="1.5" fontWeight="600"
            fontFamily="'IBM Plex Sans', sans-serif"
            style={{ pointerEvents: 'none' }}>
            {displayLabel}
          </text>
        </g>
      );
    });
  };

  // ============================================
  // RENDER
  // ============================================

  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
        <div style={styles.loadingText}>Loading Playbook Studio...</div>
      </div>
    );
  }

  // Get steps for timeline
  const maxStep = getMaxStep();
  const steps = Array.from({ length: maxStep + 1 }, (_, i) => {
    const stepPaths = paths.filter(p => p.stepNumber === i);
    const stepPasses = passes.filter(p => p.stepNumber === i);
    return {
      index: i,
      paths: stepPaths,
      passes: stepPasses,
      playerColors: stepPaths.map(p => p.color || '#3b82f6')
    };
  });

  const surfaceView = currentPlay?.courtView || surfaceViewEarly;
  const scaleY = getScaleY(currentSport, surfaceView);

  return (
    <div style={styles.container}>
      {/* LEFT — Playing Surface (60%) */}
      <div style={styles.surfacePanel} ref={surfaceRef}>
        <div
          style={{
            ...styles.surfaceArea,
            cursor: draggingPlayer ? 'grabbing' : activeTool === 'move' ? 'default' : 'default'
          }}
          onClick={handleSurfaceClick}
          onMouseMove={handleSurfaceMouseMove}
          onMouseUp={handleSurfaceMouseUp}
        >
          {currentPlay ? (
            <>
              <PlaySurfaceRenderer
                sport={currentSport}
                view={surfaceView}
                height={window.innerHeight - 140}
                showGrid={showGrid || debugMode}
              >
                {/* Movement Paths — smooth curves with chevrons */}
                {renderPaths(scaleY)}

                {/* Current drawing preview */}
                {isDrawing && drawPoints.length > 1 && (
                  <>
                    <path
                      d={smoothPath(drawPoints)}
                      fill="none" stroke="#fff" strokeWidth="1.4"
                      strokeLinecap="round" strokeLinejoin="round" opacity="0.7"
                    />
                    <path
                      d={smoothPath(drawPoints)}
                      fill="none" stroke={wheelColor} strokeWidth="0.6"
                      strokeLinecap="round" strokeLinejoin="round" opacity="0.8"
                    />
                  </>
                )}

                {/* Pass Lines */}
                {renderPasses(scaleY)}

                {/* Shot Trajectories */}
                {renderShots(scaleY)}

                {/* Screen Indicators */}
                {renderScreens(scaleY)}

                {/* Players — animated positions */}
                {renderPlayers(scaleY)}

                {/* Draggable ball — independent of player when ballPosition is set */}
                {currentPlay?.ballPosition && (() => {
                  const bx = currentPlay.ballPosition.x;
                  const by = currentPlay.ballPosition.y * scaleY;
                  return (
                    <g style={{ cursor: 'grab', pointerEvents: 'auto' }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setDraggingBall(true);
                      }}>
                      {/* Hit area for drag */}
                      <circle cx={bx} cy={by} r={4} fill="rgba(0,0,0,0.001)" />
                      <BallImage x={bx} y={by} size={BALL_SIZE} />
                    </g>
                  );
                })()}

                {/* Defenders — animated positions */}
                {renderDefenders(scaleY)}
              </PlaySurfaceRenderer>

              {/* Action Wheel */}
              {wheelVisible && (
                <ActionWheel
                  x={wheelPosition.x}
                  y={wheelPosition.y}
                  actions={wheelActions}
                  onSelect={handleActionSelect}
                  onDismiss={() => setWheelVisible(false)}
                  accentColor={wheelColor}
                  sportId={currentSport}
                  playerLabel={wheelPlayerLabel}
                />
              )}

              {/* Tool Strip */}
              <ToolStrip
                activeTool={activeTool}
                showDefenders={showDefenders}
                showGrid={showGrid}
                canUndo={undoStack.length > 0}
                canRedo={redoStack.length > 0}
                hideDefenderToggle={!!sportConfig?.hideDefenderToggle}
                onToolChange={setActiveTool}
                onToggleDefenders={() => setShowDefenders(!showDefenders)}
                onToggleGrid={() => setShowGrid(!showGrid)}
                onUndo={handleUndo}
                onRedo={handleRedo}
                onSettings={() => {}}
              />

              {/* Pending receiver selection */}
              {pendingPassToSpace && (
                <div style={{
                  position: 'absolute', bottom: 8, right: 8,
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '8px 14px',
                  background: 'rgba(251, 191, 36, 0.9)',
                  borderRadius: '8px', zIndex: 60,
                  fontFamily: "'IBM Plex Sans', sans-serif",
                }}>
                  <span style={{ color: '#000', fontSize: '13px', fontWeight: 600 }}>
                    👆 Click the player who receives
                  </span>
                  <button onClick={() => setPendingPassToSpace(null)} style={{
                    background: 'rgba(0,0,0,0.2)', border: 'none', borderRadius: 4,
                    padding: '2px 8px', color: '#000', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>✕</button>
                </div>
              )}

              {/* Action Status Bar — shows when an action is pending (waiting for click) */}
              {(isDrawing || activeTool === 'move') && (
                <div style={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  background: 'rgba(0, 0, 0, 0.8)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  zIndex: 60,
                  fontFamily: "'IBM Plex Sans', sans-serif",
                }}>
                  <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 500 }}>
                    {activeTool === 'move' ? 'Reposition — drag player'
                      : currentDrawAction ? `${currentDrawAction.name} — click target`
                      : 'Drawing...'}
                  </span>
                  {/* Same step toggle — add movement to current step (simultaneous) */}
                  {isDrawing && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setAddToCurrentStep(prev => !prev); }}
                      style={{
                        background: addToCurrentStep ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255,255,255,0.08)',
                        border: addToCurrentStep ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.15)',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        color: addToCurrentStep ? '#93c5fd' : '#64748b',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >{addToCurrentStep ? '⚡ Same Step' : '→ New Step'}</button>
                  )}
                  {/* Confirm (for reposition) */}
                  {activeTool === 'move' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setActiveTool('select'); }}
                      style={{
                        background: 'rgba(34, 197, 94, 0.2)',
                        border: '1px solid rgba(34, 197, 94, 0.5)',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        color: '#4ade80',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >✓ Done</button>
                  )}
                  {/* Cancel */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDrawing(false);
                      setDrawPoints([]);
                      setCurrentDrawAction(null);
                      if (activeTool === 'move') setActiveTool('select');
                    }}
                    style={{
                      background: 'rgba(239, 68, 68, 0.2)',
                      border: '1px solid rgba(239, 68, 68, 0.5)',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      color: '#f87171',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >✕ Cancel</button>
                </div>
              )}

              {/* Play narration moved to sidebar ControlPanel */}

              {/* Timeline Scrubber */}
              <div style={styles.timelineWrapper}>
                <TimelineScrubber
                  steps={steps}
                  currentStep={currentStep}
                  totalSteps={maxStep + 1}
                  isPlaying={isPlaying}
                  animationProgress={animationProgress}
                  speed={playbackSpeed}
                  onStepChange={setCurrentStep}
                  onPlayPause={() => setIsPlaying(!isPlaying)}
                  onSpeedChange={setPlaybackSpeed}
                  onAddStep={handleAddStep}
                  onRemoveStep={() => {}}
                  onProgressScrub={setAnimationProgress}
                  accentColor={sportConfig?.accentColor || '#3b82f6'}
                />
              </div>
            </>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>{sportConfig?.icon || '🏆'}</div>
              <div style={styles.emptyTitle}>No Play Selected</div>
              <div style={styles.emptySubtitle}>Create a new play or select one from the library</div>
              <button style={styles.createButton} onClick={handleCreatePlay}>
                + Create Play
              </button>
            </div>
          )}
        </div>

        {/* Floating buttons (top-right of surface) */}
        {currentPlay && (
          <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 15, display: 'flex', gap: 8 }}>
            <button onClick={() => {
              const entering = !debugMode;
              setDebugMode(entering);
              setActiveTool(entering ? 'move' : 'select');
              if (entering) { setWheelVisible(false); setSelectedPosition(null); }
            }} style={{
              padding: '6px 14px',
              background: debugMode ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(15, 23, 42, 0.85)',
              color: '#fff', border: debugMode ? 'none' : '1px solid rgba(255,255,255,0.15)',
              borderRadius: 8, fontSize: 11, fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
              boxShadow: debugMode ? '0 2px 8px rgba(245,158,11,0.4)' : 'none',
              backdropFilter: 'blur(12px)',
              fontFamily: "'IBM Plex Mono', monospace"
            }}>{debugMode ? 'Exit Debug' : 'Setup Debug'}</button>
            <button onClick={handleCreatePlay} style={{
              padding: '6px 14px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              boxShadow: '0 2px 8px rgba(59,130,246,0.3)'
            }}>+ New Play</button>
          </div>
        )}

        {/* Play name overlay (top-left of surface) */}
        {currentPlay && (
          <div style={styles.playNameOverlay}>
            <span style={styles.sportBadge}>{sportConfig?.icon}</span>
            <span style={styles.playNameText}>{currentPlay.name || 'Untitled Play'}</span>
            {debugMode && <span style={{ color: '#fbbf24', fontSize: 10, fontWeight: 700, marginLeft: 4 }}>DEBUG</span>}
          </div>
        )}
      </div>

      {/* RIGHT — Control Panel (40%) */}
      <div style={styles.controlPanelWrapper}>
        {/* Debug coordinate readout — sits above the control panel */}
        {debugMode && currentPlay && (
          <div style={{
            background: '#0c0f1a', borderBottom: '1px solid rgba(251, 191, 36, 0.2)',
            padding: '12px 16px',
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#fbbf24',
            flexShrink: 0
          }}>
            <div style={{ fontSize: 11, color: '#fbbf24', marginBottom: 4, fontWeight: 700, letterSpacing: 0.5 }}>
              Setup Debug
            </div>
            <div style={{ fontSize: 9, color: '#64748b', marginBottom: 8 }}>
              Drag players on surface to reposition | {currentSport} | scaleY: {scaleY.toFixed(2)}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#94a3b8', fontSize: 9, textTransform: 'uppercase' }}>
                  <td style={{ padding: '3px 6px' }}>Pos</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>X</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>Y</td>
                </tr>
              </thead>
              <tbody>
                {currentPlay.positions.map(pos => (
                  <tr key={pos.id} style={{
                    background: selectedPosition === pos.id ? 'rgba(251,191,36,0.12)' : 'transparent',
                    color: selectedPosition === pos.id ? '#fbbf24' : '#cbd5e1'
                  }}>
                    <td style={{ padding: '3px 6px', color: pos.color || '#3b82f6', fontWeight: 600 }}>
                      {pos.positionLabel}
                    </td>
                    <td style={{ padding: '3px 6px', textAlign: 'right' }}>{pos.startX}</td>
                    <td style={{ padding: '3px 6px', textAlign: 'right' }}>{pos.startY}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              onClick={() => {
                const coords = {};
                currentPlay.positions.forEach(pos => {
                  coords[pos.positionLabel] = { x: pos.startX, y: pos.startY };
                });
                navigator.clipboard?.writeText(JSON.stringify(coords, null, 2));
              }}
              style={{
                marginTop: 8, width: '100%', padding: '6px 0',
                background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)',
                borderRadius: 6, color: '#fbbf24', fontSize: 10, fontWeight: 600,
                cursor: 'pointer', fontFamily: "'IBM Plex Mono', monospace"
              }}
            >Copy Coordinates</button>

            {/* Export / Share */}
            <button
              onClick={() => {
                if (!currentPlay) return;
                // Generate text summary
                const lines = [`Play: ${currentPlay.name || 'Untitled'}\n`];
                const allEvents = [];
                (currentPlay.paths || []).forEach(path => {
                  const player = currentPlay.positions.find(p => p.id === path.positionId);
                  const label = player?.playerName || player?.positionLabel || '?';
                  const type = path.type === 'dribble' ? 'dribbles' : path.type === 'sprint' ? 'sprints' : 'runs';
                  allEvents.push({ step: path.stepNumber, text: `${label} ${type} forward` });
                });
                (currentPlay.passes || []).forEach(pass => {
                  const from = currentPlay.positions.find(p => p.id === pass.fromPositionId);
                  const to = currentPlay.positions.find(p => p.id === pass.toPositionId);
                  const fromLabel = from?.playerName || from?.positionLabel || '?';
                  const toLabel = to ? (to.playerName || to.positionLabel) : 'open space';
                  const type = pass.type === 'shoot' ? 'shoots' : pass.type === 'through-ball' ? 'plays through ball' : pass.type === 'cross' ? 'crosses' : 'passes';
                  allEvents.push({ step: pass.stepNumber, text: `${fromLabel} ${type} to ${toLabel}` });
                });
                allEvents.sort((a, b) => a.step - b.step);
                allEvents.forEach(e => lines.push(`Step ${e.step}: ${e.text}`));
                lines.push(`\nFormation: ${currentPlay.positions.map(p => p.playerName || p.positionLabel).join(', ')}`);
                const text = lines.join('\n');
                navigator.clipboard?.writeText(text);
                alert('Play copied to clipboard!\n\nPaste into text, email, or notes.');
              }}
              style={{
                marginTop: 4, width: '100%', padding: '6px 0',
                background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)',
                borderRadius: 6, color: '#60a5fa', fontSize: 10, fontWeight: 600,
                cursor: 'pointer',
              }}
            >📋 Copy Play Summary</button>

            <button
              onClick={() => {
                if (!currentPlay) return;
                const json = JSON.stringify(currentPlay, null, 2);
                const blob = new Blob([json], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${(currentPlay.name || 'play').replace(/\s+/g, '-')}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              style={{
                marginTop: 4, width: '100%', padding: '6px 0',
                background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)',
                borderRadius: 6, color: '#4ade80', fontSize: 10, fontWeight: 600,
                cursor: 'pointer',
              }}
            >💾 Export Play JSON</button>

            <button
              onClick={() => {
                // Screenshot the SVG surface
                const svg = surfaceRef.current?.querySelector('svg');
                if (!svg) return;
                const svgData = new XMLSerializer().serializeToString(svg);
                const canvas = document.createElement('canvas');
                canvas.width = 1920;
                canvas.height = Math.round(1920 * (svg.viewBox.baseVal.height / svg.viewBox.baseVal.width));
                const ctx = canvas.getContext('2d');
                const img = new Image();
                img.onload = () => {
                  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                  canvas.toBlob(blob => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${(currentPlay?.name || 'play').replace(/\s+/g, '-')}.png`;
                    a.click();
                    URL.revokeObjectURL(url);
                  });
                };
                img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
              }}
              style={{
                marginTop: 4, width: '100%', padding: '6px 0',
                background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.25)',
                borderRadius: 6, color: '#a78bfa', fontSize: 10, fontWeight: 600,
                cursor: 'pointer',
              }}
            >📸 Export as Image</button>

            <button
              onClick={handleExportGif}
              disabled={isExporting}
              style={{
                marginTop: 4, width: '100%', padding: '6px 0',
                background: isExporting ? 'rgba(251,191,36,0.3)' : 'rgba(251,191,36,0.12)',
                border: '1px solid rgba(251,191,36,0.25)',
                borderRadius: 6, color: '#fbbf24', fontSize: 10, fontWeight: 600,
                cursor: isExporting ? 'wait' : 'pointer',
              }}
            >{isExporting ? `🎬 Recording... ${exportProgress}%` : '🎬 Export Animated GIF'}</button>

            <button
              onClick={() => setShowRecorder(true)}
              style={{
                marginTop: 4, width: '100%', padding: '6px 0',
                background: 'rgba(34,197,94,0.12)',
                border: '1px solid rgba(34,197,94,0.25)',
                borderRadius: 6, color: '#4ade80', fontSize: 10, fontWeight: 600,
                cursor: 'pointer',
              }}
            >🎥 Screen Record (GIF/MP4)</button>
          </div>
        )}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <ControlPanel
            currentPlay={currentPlay}
            plays={plays}
            sportConfig={sportConfig}
            selectedPosition={selectedPosition}
            showDefenders={showDefenders}
            onPlayUpdate={handlePlayUpdate}
            onPlaySelect={handlePlaySelect}
            onPlayCreate={handleCreatePlay}
            onPlayDelete={handlePlayDelete}
            onPositionSelect={(id) => { setSelectedPosition(id); setWheelVisible(false); }}
            onFormationApply={handleFormationApply}
            onToggleDefenders={() => setShowDefenders(!showDefenders)}
            onAssignPlayer={(posId) => { setRosterAssignTarget(posId); setShowRosterPicker(true); }}
            selectedDefender={selectedDefender}
            showRoutes={showRoutes}
            onToggleRoutes={() => setShowRoutes(prev => !prev)}
            showJerseyNumbers={showJerseyNumbers}
            onToggleJerseyNumbers={() => setShowJerseyNumbers(prev => !prev)}
            onExportGif={handleExportGif}
            isExporting={isExporting}
            exportProgress={exportProgress}
            onExportImage={() => {
              const svg = surfaceRef.current?.querySelector('svg');
              if (!svg) return;
              const svgData = new XMLSerializer().serializeToString(svg);
              const canvas = document.createElement('canvas');
              canvas.width = 1920;
              canvas.height = Math.round(1920 * (svg.viewBox.baseVal.height / svg.viewBox.baseVal.width));
              const ctx = canvas.getContext('2d');
              const img = new Image();
              img.onload = () => {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                canvas.toBlob(blob => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${(currentPlay?.name || 'play').replace(/\s+/g, '-')}.png`;
                  a.click();
                  URL.revokeObjectURL(url);
                });
              };
              img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
            }}
            onExportSummary={() => {
              if (!currentPlay) return;
              const lines = [`Play: ${currentPlay.name || 'Untitled'}\n`];
              const events = [];
              (currentPlay.paths || []).forEach(p => {
                const pl = currentPlay.positions.find(pos => pos.id === p.positionId);
                events.push({ step: p.stepNumber, text: `${pl?.playerName || pl?.positionLabel || '?'} ${p.type === 'dribble' ? 'dribbles' : 'runs'} forward` });
              });
              (currentPlay.passes || []).forEach(p => {
                const from = currentPlay.positions.find(pos => pos.id === p.fromPositionId);
                const to = currentPlay.positions.find(pos => pos.id === p.toPositionId);
                events.push({ step: p.stepNumber, text: `${from?.playerName || from?.positionLabel || '?'} ${p.type === 'shoot' ? 'shoots' : 'passes'} to ${to?.playerName || to?.positionLabel || 'space'}` });
              });
              events.sort((a, b) => a.step - b.step);
              events.forEach(e => lines.push(`Step ${e.step}: ${e.text}`));
              navigator.clipboard?.writeText(lines.join('\n'));
              alert('Play copied to clipboard!');
            }}
            onExportJson={() => {
              if (!currentPlay) return;
              const blob = new Blob([JSON.stringify(currentPlay, null, 2)], { type: 'application/json' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `${(currentPlay.name || 'play').replace(/\s+/g, '-')}.json`;
              a.click();
            }}
            onDefenderSelect={(defId) => { setSelectedDefender(defId); setSelectedPosition(null); setWheelVisible(false); }}
            lineups={lineups}
            onSaveLineup={async (name) => {
              if (!currentPlay) return;
              const lineup = {
                id: `lineup-${Date.now()}`,
                name,
                formation: currentPlay.formation || '',
                assignments: currentPlay.positions.map(p => ({
                  positionLabel: p.positionLabel,
                  playerId: p.playerId || null,
                  playerName: p.playerName || '',
                  jerseyNumber: p.jerseyNumber || null,
                  jerseyColorPreset: p.jerseyColorPreset,
                  shortsColorPreset: p.shortsColorPreset,
                  isGoalkeeper: p.positionLabel === 'GK',
                })),
                createdAt: new Date().toISOString(),
              };
              const updated = [...lineups, lineup];
              setLineups(updated);
              await setSetting(`lineups-${currentSport}`, updated);
            }}
            onLoadLineup={(lineup) => {
              if (!currentPlay) return;
              const updatedPositions = currentPlay.positions.map(pos => {
                const match = lineup.assignments.find(a => a.positionLabel === pos.positionLabel);
                if (match && match.playerId) {
                  return {
                    ...pos,
                    playerId: match.playerId,
                    playerName: match.playerName,
                    jerseyNumber: match.jerseyNumber,
                    jerseyColorPreset: match.jerseyColorPreset || pos.jerseyColorPreset,
                    shortsColorPreset: match.shortsColorPreset || pos.shortsColorPreset,
                  };
                }
                return pos;
              });
              handlePlayUpdate({ ...currentPlay, positions: updatedPositions, lastModified: new Date().toISOString() });
            }}
            onDeleteLineup={async (id) => {
              const updated = lineups.filter(l => l.id !== id);
              setLineups(updated);
              await setSetting(`lineups-${currentSport}`, updated);
            }}
            onQuickTactic={(tactic) => {
              if (!currentPlay) return;
              const positions = currentPlay.positions || [];
              const paths = currentPlay.paths || [];

              if (tactic === 'face-ball') {
                // Face all players toward the ball
                const bp = ballWorldPos;
                if (!bp) return;
                const updated = positions.map(p => {
                  const pos = getAnimatedPosition(p, currentStep, isPlaying, animationProgress, positions, paths);
                  if (!pos) return p;
                  const dx = bp.x - pos.x;
                  const dy = bp.y - pos.y;
                  if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return p;
                  return { ...p, facingDirection: angleToBearing(dx, dy) };
                });
                handlePlayUpdate({ ...currentPlay, positions: updated, lastModified: new Date().toISOString() });
              }

              else if (tactic === 'face-opponent-goal') {
                const updated = positions.map(p => ({ ...p, facingDirection: 'S' }));
                handlePlayUpdate({ ...currentPlay, positions: updated, lastModified: new Date().toISOString() });
              }

              else if (tactic === 'face-own-goal') {
                const updated = positions.map(p => ({ ...p, facingDirection: 'N' }));
                handlePlayUpdate({ ...currentPlay, positions: updated, lastModified: new Date().toISOString() });
              }

              else if (tactic === 'face-auto') {
                const updated = positions.map(p => ({ ...p, facingDirection: null }));
                handlePlayUpdate({ ...currentPlay, positions: updated, lastModified: new Date().toISOString() });
              }

              else if (tactic === 'crash-goal') {
                // Create a new step where all players within ~18 data units of goal run toward it
                const goalY = 98; // opponent goal in data space
                const goalX = 50;
                const crashRadius = 18; // ~20 yards in data units
                const maxStep = Math.max(0, ...(paths.map(p => p.stepNumber || 0)), ...(currentPlay.passes || []).map(p => p.stepNumber || 0), ...(currentPlay.shots || []).map(s => s.stepNumber || 0));
                const crashStep = maxStep + 1;
                const newPaths = [];

                positions.forEach(pos => {
                  const curPos = getPlayerPositionAtStep(pos.id, maxStep, positions, paths) || { x: pos.startX, y: pos.startY };
                  const dist = Math.sqrt((curPos.x - goalX) ** 2 + (curPos.y - goalY) ** 2);
                  if (dist < crashRadius && dist > 2) {
                    // Run toward the goal area with slight spread
                    const spreadX = goalX + (curPos.x - goalX) * 0.15;
                    const targetY = Math.min(goalY + 1, curPos.y + (goalY - curPos.y) * 0.85);
                    newPaths.push({
                      id: `path-crash-${Date.now()}-${pos.id}`,
                      positionId: pos.id,
                      type: 'sprint',
                      points: [curPos, { x: spreadX, y: targetY }],
                      lineStyle: 'dashed',
                      stepNumber: crashStep,
                      color: pos.color || '#f59e0b',
                    });
                  }
                });

                if (newPaths.length > 0) {
                  handlePlayUpdate({
                    ...currentPlay,
                    paths: [...paths, ...newPaths],
                    lastModified: new Date().toISOString(),
                  });
                  setCurrentStep(crashStep);
                }
              }

              else if (tactic === 'defense-up') {
                // Move all defenders up toward midfield (reduce Y by ~15 data units)
                if (!showDefenders || !currentPlay.defenders?.length) return;
                const defPaths = currentPlay.defenderPaths || [];
                const maxStep = Math.max(0, ...(paths.map(p => p.stepNumber || 0)), ...(currentPlay.passes || []).map(p => p.stepNumber || 0), ...(defPaths.map(p => p.stepNumber || 0)));
                const moveStep = maxStep + 1;
                const newDefPaths = [];

                currentPlay.defenders.forEach(def => {
                  const curPos = getDefenderPosition(def, defPaths, maxStep, false, 0);
                  const targetY = Math.max(curPos.y - 15, 35); // Don't go past midfield
                  if (curPos.y - targetY > 2) {
                    newDefPaths.push({
                      id: `defpath-up-${Date.now()}-${def.id}`,
                      defenderId: def.id,
                      type: 'movement',
                      points: [{ x: curPos.x, y: curPos.y }, { x: curPos.x, y: targetY }],
                      lineStyle: 'dashed',
                      stepNumber: moveStep,
                    });
                  }
                });

                if (newDefPaths.length > 0) {
                  handlePlayUpdate({
                    ...currentPlay,
                    defenderPaths: [...defPaths, ...newDefPaths],
                    lastModified: new Date().toISOString(),
                  });
                  setCurrentStep(moveStep);
                }
              }
            }}
            spriteSize={spriteSize}
            onSpriteSizeChange={setSpriteSize}
            colorMode={colorMode}
            onColorModeChange={setColorMode}
            currentStep={currentStep}
            surfaceView={surfaceView}
            sportViews={sportConfig?.views || ['full']}
            onViewChange={(view) => {
              if (!currentPlay) return;
              handlePlayUpdate({ ...currentPlay, courtView: view, lastModified: new Date().toISOString() });
            }}
            onNextStep={handleAddStep}
            onScreenRecord={() => setShowRecorder(true)}
            addToCurrentStep={addToCurrentStep}
            onStepModeChange={setAddToCurrentStep}
            onFacingChange={(id, direction) => {
              if (!currentPlay) return;
              const isPlayer = currentPlay.positions.some(p => p.id === id);
              const updated = {
                ...currentPlay,
                positions: isPlayer
                  ? currentPlay.positions.map(p => p.id === id ? { ...p, facingDirection: direction } : p)
                  : currentPlay.positions,
                defenders: !isPlayer
                  ? (currentPlay.defenders || []).map(d => d.id === id ? { ...d, facingDirection: direction } : d)
                  : (currentPlay.defenders || []),
                lastModified: new Date().toISOString(),
              };
              handlePlayUpdate(updated);
            }}
          />
        </div>
      </div>

      {/* Roster Picker Modal */}
      {showRosterPicker && (
        <RosterPicker
          isOpen={showRosterPicker}
          onClose={() => { setShowRosterPicker(false); setRosterAssignTarget(null); }}
          onSelectPlayer={handleRosterAssign}
          currentPosition={currentPlay?.positions?.find(p => p.id === rosterAssignTarget)}
          excludePlayerIds={(currentPlay?.positions || []).filter(p => p.playerId).map(p => p.playerId)}
        />
      )}

      {/* Screen Recorder overlay */}
      {showRecorder && (
        <ScreenRecorder
          surfaceRef={surfaceRef}
          isPlayingBack={isPlaying}
          onClose={() => setShowRecorder(false)}
          onStartPlayback={() => {
            setCurrentStep(0);
            setAnimationProgress(0);
            setIsPlaying(true);
          }}
          onStopPlayback={() => {
            setIsPlaying(false);
            setCurrentStep(0);
            setAnimationProgress(0);
          }}
        />
      )}
    </div>
  );
};

// ============================================
// STYLES
// ============================================

const styles = {
  container: {
    display: 'flex',
    height: '100vh',
    width: '100%',
    overflow: 'hidden',
    background: '#0a0e17',
    fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif"
  },
  surfacePanel: {
    flex: '0 0 60%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    background: '#0f1520',
    borderRight: '1px solid #1e293b'
  },
  surfaceArea: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    position: 'relative',
    overflow: 'hidden'
  },
  timelineWrapper: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 20
  },
  playNameOverlay: {
    position: 'absolute',
    top: '50px',
    left: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 14px',
    background: 'rgba(15, 23, 42, 0.85)',
    backdropFilter: 'blur(12px)',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.08)',
    zIndex: 15
  },
  sportBadge: {
    fontSize: '16px'
  },
  playNameText: {
    color: '#f1f5f9',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '0.3px'
  },
  controlPanelWrapper: {
    flex: '0 0 40%',
    maxWidth: '480px',
    overflow: 'hidden',
    background: '#0f172a',
    borderLeft: '1px solid #1e293b',
    display: 'flex',
    flexDirection: 'column'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    color: '#64748b'
  },
  emptyIcon: {
    fontSize: '64px',
    opacity: 0.5
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#94a3b8'
  },
  emptySubtitle: {
    fontSize: '14px',
    color: '#64748b'
  },
  createButton: {
    marginTop: '12px',
    padding: '10px 24px',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#0a0e17',
    gap: '16px'
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #1e293b',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  },
  loadingText: {
    color: '#64748b',
    fontSize: '14px',
    fontWeight: '500'
  }
};

export default PlaybookStudio;
